/**
 * Quote Templates — Phase B3 (read-only viewer)
 *
 * Routes:
 *   /quote-templates       → list of all templates for this org + community
 *   /quote-templates/:id   → tree view of one template + live preview pane
 *
 * Editing lands in Phase B4. For now you can browse, see the decision tree
 * that produces every generator quote, change sample inputs to see what
 * different inputs would yield, and view version history.
 *
 * The whole page is a single component so it can share styles and the
 * api-call helper. Sub-components are defined below the main export.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";

// ── Color tokens (match QuoteDraftReview) ────────────────────────────────────
const C = {
  bg:        "#0d0f12",
  surface:   "#161a20",
  surface2:  "#1e2328",
  border:    "#2a3140",
  accent:    "#a5ce3a",
  accentDim: "rgba(165,206,58,.12)",
  text:      "#e8eaf0",
  muted:     "#8892a4",
  warn:      "#f59e0b",
  danger:    "#ef4444",
  good:      "#10b981",
};

const fmt$ = n => "$" + Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Each node_type gets an icon + label so the tree reads like the design we
// agreed on (🎯 tier classifier, 📦 material, etc.)
const NODE_TYPE_META = {
  tier_classifier:  { icon: "🎯", label: "Tier classifier",        color: C.accent },
  material:         { icon: "📦", label: "Material",               color: "#7dd3fc" },
  material_picker:  { icon: "🎲", label: "Material picker",        color: "#fbbf24" },
  scope_item:       { icon: "📝", label: "Scope item",             color: C.text },
  note:             { icon: "📌", label: "Note",                   color: C.muted },
  exclusion:        { icon: "🚫", label: "Exclusion",              color: "#f87171" },
  labour_rule:      { icon: "💼", label: "Labour rule",            color: "#a78bfa" },
  hard_fail:        { icon: "🛡️", label: "Hard-fail check",        color: C.warn },
  condition_group:  { icon: "📁", label: "Condition group",        color: C.muted },
};

// API helper
async function call(action, payload = {}) {
  const r = await apiFetch("/api/quote-brain", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ action, ...payload }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

// ── Rule Recipes (Zapier-style starter rules) ───────────────────────────────
// Each recipe is a pre-filled config Dylan can drop into a template, then
// customize. The keys mirror node_type. New recipes go here; nothing in
// the API needs to change. Recipes are pure UI sugar over createNode.
const RULE_RECIPES = {
  scope_item: [
    { key: "blank",         label: "Blank scope item",        desc: "Empty starter", config: { text_template: "New scope item" } },
    { key: "permit",        label: "Permit + inspection",     desc: "TSBC permit boilerplate", config: { text_template: "Electrical permit (TSBC) + final inspection" } },
    { key: "bc_hydro",      label: "BC Hydro coordination",   desc: "Disconnect / reconnect scheduling", config: { text_template: "Coordinate BC Hydro service disconnect + reconnect (scheduled 2 weeks ahead)" } },
    { key: "reterminate",   label: "Re-terminate circuits",   desc: "Branch circuits → new panel", config: { text_template: "Re-terminate all existing branch circuits into new panel and label panel directory" } },
    { key: "commission",    label: "Commissioning + walkthrough", desc: "Test + train customer", config: { text_template: "Test equipment operation + client walk-through + label / register as required" } },
    { key: "tier_gated",    label: "Tier-gated step",         desc: "Only fires for one tier", config: { include_if: { field: "tier", op: "==", value: "REPLACE_ME" }, text_template: "Tier-specific scope step" } },
    { key: "if_true",       label: "Conditional step",        desc: "Fires when a boolean field is true", config: { include_if: { field: "REPLACE_FIELD", op: "is_true" }, text_template: "Conditional scope step" } },
  ],
  note: [
    { key: "blank",         label: "Blank note",              desc: "Empty starter", config: { text_template: "New note" } },
    { key: "warranty_1yr",  label: "Workmanship warranty (1yr)", desc: "Browning standard short", config: { text_template: "1-year Browning Electric workmanship warranty on installation" } },
    { key: "warranty_3yr",  label: "Labour warranty (3yr)",   desc: "Browning premium scope", config: { text_template: "3-year Browning Electric labour warranty" } },
    { key: "permit_included", label: "Permit included",       desc: "TSBC permit covered", config: { text_template: "Electrical permit (TSBC) + final inspection included in scope" } },
    { key: "bc_hydro_fee",  label: "BC Hydro fee separate",   desc: "Standard disclosure", config: { text_template: "BC Hydro service fees billed direct by BC Hydro (~$250-260)" } },
    { key: "rebate",        label: "Rebate eligibility",      desc: "Customer-facing value add", config: { text_template: "Customer may be eligible for BC Hydro / CleanBC rebate — verify on the BC Hydro website" } },
    { key: "tier_gated",    label: "Tier-gated note",         desc: "Only shows for one tier", config: { include_if: { field: "tier", op: "==", value: "REPLACE_ME" }, text_template: "Tier-specific note" } },
  ],
  exclusion: [
    { key: "blank",         label: "Blank exclusion",         desc: "Empty starter", config: { text_template: "Not included: …" } },
    { key: "drywall",       label: "Drywall / paint repair",  desc: "Standard Browning carve-out", config: { text_template: "Drywall / paint repair around equipment location (by others)" } },
    { key: "bc_hydro_fee",  label: "BC Hydro service fees",   desc: "Billed direct by BC Hydro", config: { text_template: "BC Hydro service fees + reconnection fees (~$250-260, billed direct by BC Hydro)" } },
    { key: "existing_def",  label: "Existing deficiencies",   desc: "Pre-existing wiring issues", config: { text_template: "Repairs to existing branch-circuit wiring or pre-existing deficiencies discovered during install (separate scope)" } },
    { key: "trenching",     label: "Trenching by others",     desc: "Excludes excavation", config: { text_template: "Trenching / excavation by others, unless explicitly included in scope" } },
    { key: "appliance",     label: "Appliance damage",        desc: "Existing wiring blame", config: { text_template: "Repairs to appliances damaged by pre-existing wiring conditions discovered during work" } },
    { key: "tier_gated",    label: "Tier-gated exclusion",    desc: "Only for one tier", config: { include_if: { field: "tier", op: "==", value: "REPLACE_ME" }, text_template: "Tier-specific exclusion" } },
  ],
  hard_fail: [
    { key: "blank",         label: "Blank check",             desc: "Empty starter", config: { code: "new_check", severity: "warn", message_template: "New check", condition: true } },
    { key: "missing_info",  label: "Missing-info check",      desc: "Warn when a field is null", config: { code: "REPLACE_field_missing", severity: "info", message_template: "Field not provided — quote uses defaults. Confirm with customer.", condition: { field: "REPLACE_FIELD", op: "is_null" } } },
    { key: "tier_warning",  label: "Tier-specific warning",   desc: "Fires only for one tier", config: { code: "REPLACE_tier_check", severity: "warn", message_template: "Tier-specific reminder", condition: { field: "tier", op: "==", value: "REPLACE_TIER" } } },
    { key: "required",      label: "Required-field error",    desc: "Block-style: severity=error", config: { code: "REPLACE_field_required", severity: "error", message_template: "Required field missing — cannot produce a confident quote without this.", condition: { field: "REPLACE_FIELD", op: "is_null" } } },
    { key: "panel_capacity", label: "Panel capacity unconfirmed", desc: "Standard EV / Generator guardrail", config: { code: "panel_capacity_unconfirmed", severity: "warn", message_template: "Panel main amperage + existing load not confirmed — load management or upgrade may be required.", condition: { field: "panel_amps", op: "is_null" } } },
    { key: "bc_hydro_lead", label: "BC Hydro lead time",     desc: "2-week scheduling reminder", config: { code: "bc_hydro_lead_time", severity: "warn", message_template: "BC Hydro disconnect must be scheduled at least 2 weeks ahead.", condition: true } },
  ],
  material: [
    { key: "blank",         label: "Blank material",          desc: "Empty starter", config: { sku_template: "NEW_ITEM", description_template: "New material", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
    { key: "fixed_qty",     label: "Fixed quantity item",     desc: "Each, lot, ea — known qty", config: { sku_template: "REPLACE_SKU", description_template: "Item description", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0, lookup: { patterns_template: ["search keyword 1","search keyword 2"], supplier_preference: ["EB Horsman","City Electric"] } } },
    { key: "length_cable",  label: "Length-driven cable",     desc: "Qty pulled from a request field", config: { sku_template: "CABLE_REPLACE", description_template: "Cable description", qty: { field: "feed_length_m", default: 15 }, unit: "m", markup_pct: 35, pst_pct: 7, fallback_price: 8, lookup: { patterns_template: ["cable type keyword"], supplier_preference: ["EB Horsman","City Electric"], min_price: 2, max_price: 50 } } },
    { key: "misc_allowance", label: "Misc allowance",         desc: "Lot for fittings / consumables", config: { sku_template: "MISC_ALLOWANCE", description_template: "Misc connectors, straps, fittings, labels", qty: 1, unit: "lot", markup_pct: 35, pst_pct: 7, fallback_price: 250 } },
    { key: "tier_gated",    label: "Tier-gated material",     desc: "Only included for one tier", config: { include_if: { field: "tier", op: "==", value: "REPLACE_TIER" }, sku_template: "REPLACE_SKU", description_template: "Tier-specific material", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
    { key: "conditional",   label: "Conditional material",    desc: "Fires when a boolean is true", config: { include_if: { field: "REPLACE_FIELD", op: "is_true" }, sku_template: "REPLACE_SKU", description_template: "Conditional material", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
  ],
  material_picker: [
    { key: "blank",         label: "Blank picker",            desc: "Single default rule", config: { selection_rules: [{ default: true, set: { sku_template: "REPLACE_SKU", description_template: "Default option", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } }] } },
    { key: "per_tier",      label: "Per-tier picker",         desc: "Different SKU per tier", config: { selection_rules: [
        { if: { field: "tier", op: "==", value: "TIER_A" }, set: { sku_template: "SKU_A", description_template: "Option for Tier A", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { if: { field: "tier", op: "==", value: "TIER_B" }, set: { sku_template: "SKU_B", description_template: "Option for Tier B", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { default: true,                                    set: { sku_template: "SKU_DEFAULT", description_template: "Default option", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
      ]} },
    { key: "per_amp",       label: "Per-amperage picker",     desc: "Branches by amp / kW value", config: { selection_rules: [
        { if: { field: "REPLACE_AMP_FIELD", op: "==", value: 100 }, set: { sku_template: "SKU_100A", description_template: "100A option",  qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { if: { field: "REPLACE_AMP_FIELD", op: "==", value: 200 }, set: { sku_template: "SKU_200A", description_template: "200A option",  qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { default: true,                                            set: { sku_template: "SKU_DEFAULT", description_template: "Default option", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
      ]} },
    { key: "customer_choice", label: "Customer-choice picker", desc: "Option A vs B (customer-selectable)", config: { selection_rules: [
        { if: { field: "REPLACE_CHOICE_FIELD", op: "==", value: "option_a" }, set: { sku_template: "SKU_A", description_template: "Option A",  qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { if: { field: "REPLACE_CHOICE_FIELD", op: "==", value: "option_b" }, set: { sku_template: "SKU_B", description_template: "Option B",  qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
        { default: true,                                                      set: { sku_template: "SKU_A", description_template: "Option A (default)", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } },
      ]} },
  ],
  labour_rule: [
    { key: "blank",         label: "Blank labour rule",       desc: "Base + adjustments + rate options", config: { base_hours: 8, adjustments: [], rate_options: [{ name: "standard", rate: 280, default: true }] } },
    { key: "tier_gated",    label: "Tier-gated labour",       desc: "Only fires for one tier", config: { include_if: { field: "tier", op: "==", value: "REPLACE_TIER" }, base_hours: 8, adjustments: [
        { name: "Adjustment 1 description", if: { field: "REPLACE_FIELD", op: "is_true" }, hours: 2 },
      ], rate_options: [{ name: "standard", rate: 280, default: true }] } },
    { key: "browning_std",  label: "Browning standard (8h)",  desc: "Standard half-day install + common adjustments", config: { base_hours: 8, adjustments: [
        { name: "Outdoor mount + WP",       if: { field: "outdoor_mount",     op: "is_true" },        hours: 1 },
        { name: "Long cable run (>30m)",    if: { field: "feed_length_m",     op: ">", value: 30 },   hours: 2 },
        { name: "30+ branch circuits",      if: { field: "branch_circuit_count", op: ">", value: 30 }, hours: 3 },
      ], rate_options: [{ name: "standard", rate: 280, default: true }] } },
    { key: "browning_big",  label: "Browning whole-day (16h)", desc: "Service upgrade / generator / large scope", config: { base_hours: 16, adjustments: [
        { name: "Trenching",                if: { field: "trenching_required", op: "is_true" },       hours: 6 },
        { name: "BC Hydro coordination",    if: { field: "amperage_upgraded",  op: "is_true" },       hours: 1 },
        { name: "Drywall cut + access",     if: { field: "drywall_required",   op: "is_true" },       hours: 2 },
      ], rate_options: [{ name: "standard", rate: 280, default: true }] } },
  ],
};

// ═════════════════════════════════════════════════════════════════════════════
// Condition vocabulary — JSON ↔ plain English translation
//
// Every condition in a playbook (include_if, tier_classifier.rules.if,
// material_picker.selection_rules.if, labour_rule.adjustments.if, hard_fail.condition)
// uses the same shape. Translating that to readable language is the difference
// between "code that scares non-developers" and "rules anyone can edit."
// ═════════════════════════════════════════════════════════════════════════════

// Operators in the order they appear in dropdowns. valueKind drives the
// input UI: "input" (free text), "number" (number input), "none" (no value),
// "list" (comma-separated multi).
const OPERATOR_LIBRARY = [
  { op: "==",          label: "is",              valueKind: "input"  },
  { op: "!=",          label: "is not",          valueKind: "input"  },
  { op: ">",           label: "is more than",    valueKind: "number" },
  { op: "<",           label: "is less than",    valueKind: "number" },
  { op: ">=",          label: "is at least",     valueKind: "number" },
  { op: "<=",          label: "is at most",      valueKind: "number" },
  { op: "in",          label: "is one of",       valueKind: "list"   },
  { op: "contains",    label: "contains",        valueKind: "input"  },
  { op: "is_true",     label: "is yes / on",     valueKind: "none"   },
  { op: "is_false",    label: "is no / off",     valueKind: "none"   },
  { op: "is_null",     label: "is not set",      valueKind: "none"   },
  { op: "is_not_null", label: "is set",          valueKind: "none"   },
];
const OPS_BY_KEY = Object.fromEntries(OPERATOR_LIBRARY.map(o => [o.op, o]));

// Standard fields that should appear in the field picker even if the current
// playbook hasn't referenced them yet. Tier is special (its known values come
// from the tier_classifier).
const STANDARD_FIELDS = [
  { name: "tier",                  label: "Tier",                   type: "enum"    },
  { name: "service_type",          label: "Service type",           type: "string"  },
  { name: "panel_amps",            label: "Panel amps",             type: "number"  },
  { name: "new_amps",              label: "New (target) amps",      type: "number"  },
  { name: "load_calc_amps",        label: "Load-calc amps",         type: "number"  },
  { name: "feed_length_m",         label: "Feed length (m)",        type: "number"  },
  { name: "ug_run_m",              label: "Underground run (m)",    type: "number"  },
  { name: "oh_run_m",              label: "Overhead run (m)",       type: "number"  },
  { name: "ev_charger_amps",       label: "EV charger amps",        type: "number"  },
  { name: "genny_kw",              label: "Generator size (kW)",    type: "number"  },
  { name: "outdoor_mount",         label: "Outdoor mount",          type: "boolean" },
  { name: "hardwired",             label: "Hardwired",              type: "boolean" },
  { name: "client_owns_charger",   label: "Client owns charger",    type: "boolean" },
  { name: "client_owns_generator", label: "Client owns generator",  type: "boolean" },
  { name: "trenching_required",    label: "Trenching required",     type: "boolean" },
  { name: "drywall_required",      label: "Drywall cutting needed", type: "boolean" },
  { name: "hot_tub_on_backup",     label: "Hot tub on backup",      type: "boolean" },
  { name: "service_upgrade_combo", label: "Service upgrade combo",  type: "boolean" },
  { name: "hazardous_brand_flag",  label: "Hazardous panel brand",  type: "boolean" },
];
const STANDARD_FIELDS_BY_NAME = Object.fromEntries(STANDARD_FIELDS.map(f => [f.name, f]));

// Turn a snake_case field name into a friendly label. Looks up the standard
// dictionary first, then falls back to a title-case transform.
function friendlyField(name) {
  if (STANDARD_FIELDS_BY_NAME[name]) return STANDARD_FIELDS_BY_NAME[name].label;
  if (!name) return "(unknown field)";
  return String(name)
    .replace(/_/g, " ")
    .replace(/\bamps?\b/gi, "amps")
    .replace(/\bkw\b/gi, "kW")
    .replace(/\bev\b/gi, "EV")
    .replace(/\bug\b/gi, "UG")
    .replace(/\bgfci\b/gi, "GFCI")
    .replace(/\bbc\b/gi, "BC")
    .replace(/^./, c => c.toUpperCase());
}

// Look up a friendlier value display for tier slugs (uses tierLabels map if
// provided). Booleans render as Yes/No, lists as "A, B, C", everything else
// passes through unchanged.
function formatValue(val, fieldName, tierLabels) {
  if (val == null || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (Array.isArray(val)) return val.map(v => formatValue(v, fieldName, tierLabels)).join(", ");
  if (fieldName === "tier" && tierLabels && tierLabels[val]) return `"${tierLabels[val]}"`;
  if (typeof val === "string") return `"${val}"`;
  return String(val);
}

// Turn a condition (JSON or boolean) into a one-sentence English description.
// Used in node card summaries AND inside the ConditionBuilder for live preview.
function describeCondition(cond, opts = {}) {
  if (cond == null) return "Always";
  if (cond === true)  return "Always";
  if (cond === false) return "Never";
  if (typeof cond !== "object") return String(cond);

  if (Array.isArray(cond.and)) {
    if (cond.and.length === 0) return "Always";
    if (cond.and.length === 1) return describeCondition(cond.and[0], opts);
    return cond.and.map(c => describeCondition(c, opts)).join(" AND ");
  }
  if (Array.isArray(cond.or)) {
    if (cond.or.length === 0) return "Always";
    if (cond.or.length === 1) return describeCondition(cond.or[0], opts);
    return "(" + cond.or.map(c => describeCondition(c, opts)).join(" OR ") + ")";
  }

  if (cond.field) {
    const field = friendlyField(cond.field);
    const op    = cond.op;
    const value = formatValue(cond.value, cond.field, opts.tierLabels);
    switch (op) {
      case "==":          return `${field} is ${value}`;
      case "!=":          return `${field} is not ${value}`;
      case ">":           return `${field} is more than ${value}`;
      case "<":           return `${field} is less than ${value}`;
      case ">=":          return `${field} is at least ${value}`;
      case "<=":          return `${field} is at most ${value}`;
      case "in":          return `${field} is one of ${value}`;
      case "contains":    return `${field} contains ${value}`;
      case "is_true":     return `${field} is yes`;
      case "is_false":    return `${field} is no`;
      case "is_null":     return `${field} is not set`;
      case "is_not_null": return `${field} is set`;
      default:            return `${field} ${op} ${value}`;
    }
  }

  return JSON.stringify(cond);
}

// Walk a playbook's nodes to find every field name referenced in any
// condition, plus pull the known tier labels from the tier_classifier.
// Used by ConditionBuilder so the Field dropdown auto-populates with the
// fields this playbook actually uses.
function collectPlaybookSchema(nodes) {
  const fieldNames = new Set();
  const enumValues = {}; // field → Set<value>
  let tierLabels = {};
  const walk = (c) => {
    if (!c || typeof c !== "object") return;
    if (c.field) {
      fieldNames.add(c.field);
      if (c.value != null && typeof c.value !== "object") {
        enumValues[c.field] = enumValues[c.field] || new Set();
        enumValues[c.field].add(c.value);
      }
      if (Array.isArray(c.value)) c.value.forEach(v => {
        enumValues[c.field] = enumValues[c.field] || new Set();
        enumValues[c.field].add(v);
      });
    }
    if (Array.isArray(c.and)) c.and.forEach(walk);
    if (Array.isArray(c.or))  c.or.forEach(walk);
  };
  for (const n of (nodes || [])) {
    const c = n.config || {};
    walk(c.include_if);
    walk(c.condition);
    for (const r of (c.rules || c.selection_rules || c.post || [])) walk(r.if);
    for (const a of (c.adjustments || [])) walk(a.if);
    if (c.qty && typeof c.qty === "object" && c.qty.field) fieldNames.add(c.qty.field);
    // Tier classifier — capture tier slug → label mapping
    if (n.node_type === "tier_classifier") {
      for (const r of (c.rules || [])) {
        if (r.set?.tier && r.set?.tier_label) tierLabels[r.set.tier] = r.set.tier_label;
        if (r.set?.tier) {
          enumValues.tier = enumValues.tier || new Set();
          enumValues.tier.add(r.set.tier);
        }
      }
    }
  }
  // Merge standard fields in (so the dropdown always offers them)
  for (const sf of STANDARD_FIELDS) fieldNames.add(sf.name);
  // Build the final field list with type info
  const fields = Array.from(fieldNames).sort().map(name => {
    const std = STANDARD_FIELDS_BY_NAME[name];
    return {
      name,
      label: friendlyField(name),
      type:  std?.type || (enumValues[name] ? "enum" : "string"),
      knownValues: enumValues[name] ? Array.from(enumValues[name]).sort() : [],
    };
  });
  return { fields, tierLabels };
}

// ─────────────────────────────────────────────────────────────────────────────
// ConditionBuilder — visual editor for a condition. Renders three modes:
//   1. "Always" (null / true / empty)  →  single radio + Add Condition button
//   2. Simple condition (single leaf)  →  [Field][Op][Value] chips + +AND / +OR
//   3. Group condition (AND/OR array)  →  list of rows + add buttons
// All edits keep the underlying JSON shape that the executor already speaks.
// ─────────────────────────────────────────────────────────────────────────────
function ConditionBuilder({ value, onChange, schema, label }) {
  const fields     = schema?.fields || STANDARD_FIELDS;
  const tierLabels = schema?.tierLabels || {};

  // Normalize the current value into one of three modes for rendering:
  const isAlways = value == null || value === true;
  const isGroup  = !isAlways && typeof value === "object" && (Array.isArray(value.and) || Array.isArray(value.or));
  const isSimple = !isAlways && !isGroup && typeof value === "object" && value.field;

  // Helper: convert "always" → a starter simple leaf
  function startSimple() {
    onChange({ field: fields[0]?.name || "tier", op: "==", value: "" });
  }
  // Helper: promote a simple leaf into an AND group (so the user can add more)
  function promoteToAnd() {
    onChange({ and: [value, { field: fields[0]?.name || "tier", op: "==", value: "" }] });
  }
  function promoteToOr() {
    onChange({ or: [value, { field: fields[0]?.name || "tier", op: "==", value: "" }] });
  }
  // For groups: update a single child
  function updateChild(groupKey, idx, newChild) {
    const arr = value[groupKey].slice();
    arr[idx] = newChild;
    onChange({ ...value, [groupKey]: arr });
  }
  // For groups: add a child
  function addChild(groupKey) {
    onChange({
      ...value,
      [groupKey]: [...value[groupKey], { field: fields[0]?.name || "tier", op: "==", value: "" }],
    });
  }
  // For groups: remove a child (if down to 1, collapse to that leaf)
  function removeChild(groupKey, idx) {
    const arr = value[groupKey].filter((_, i) => i !== idx);
    if (arr.length === 0) onChange(null);
    else if (arr.length === 1) onChange(arr[0]);
    else onChange({ ...value, [groupKey]: arr });
  }
  // Flip AND ↔ OR for a group
  function flipGroup() {
    if (value.and) onChange({ or: value.and });
    else if (value.or) onChange({ and: value.or });
  }

  return (
    <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
      {label && <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>{label}</div>}

      {/* Always vs Conditional toggle */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8, fontSize: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="radio" checked={isAlways} onChange={() => onChange(null)} />
          <span>Always</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="radio" checked={!isAlways} onChange={() => { if (isAlways) startSimple(); }} />
          <span>Only when…</span>
        </label>
      </div>

      {isSimple && (
        <ConditionLeafRow
          leaf={value}
          fields={fields}
          tierLabels={tierLabels}
          onChange={(nl) => onChange(nl)}
          onRemove={() => onChange(null)}
        />
      )}

      {isGroup && (
        <div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
            <button
              onClick={flipGroup}
              style={{ ...btn("ghost"), padding: "2px 8px", fontSize: 11 }}
              title={`Switch to ${value.and ? "ANY (OR)" : "ALL (AND)"}`}
            >
              {value.and ? "ALL of these are true" : "ANY of these is true"} ⇆
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(value.and || value.or).map((child, idx) => (
              <ConditionLeafRow
                key={idx}
                leaf={child}
                fields={fields}
                tierLabels={tierLabels}
                onChange={(nc) => updateChild(value.and ? "and" : "or", idx, nc)}
                onRemove={() => removeChild(value.and ? "and" : "or", idx)}
              />
            ))}
          </div>
        </div>
      )}

      {!isAlways && (
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          {isSimple && (
            <>
              <button onClick={promoteToAnd} style={{ ...btn("ghost"), fontSize: 11, padding: "4px 10px" }}>+ AND another</button>
              <button onClick={promoteToOr}  style={{ ...btn("ghost"), fontSize: 11, padding: "4px 10px" }}>+ OR another</button>
            </>
          )}
          {isGroup && (
            <button
              onClick={() => addChild(value.and ? "and" : "or")}
              style={{ ...btn("ghost"), fontSize: 11, padding: "4px 10px" }}
            >
              + Add condition
            </button>
          )}
        </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
        Preview: <span style={{ color: C.text }}>{describeCondition(value, { tierLabels })}</span>
      </div>
    </div>
  );
}

// Single leaf condition row: [Field ▾] [Op ▾] [Value]  [×]
function ConditionLeafRow({ leaf, fields, tierLabels, onChange, onRemove }) {
  const fieldName = leaf.field || "";
  const fieldInfo = fields.find(f => f.name === fieldName) || { type: "string", knownValues: [] };
  const opInfo    = OPS_BY_KEY[leaf.op] || OPS_BY_KEY["=="];
  const valueKind = opInfo.valueKind;

  // Auto-narrow the op list based on field type for sanity
  const validOps = OPERATOR_LIBRARY.filter(o => {
    if (fieldInfo.type === "boolean") return ["is_true", "is_false", "is_null", "is_not_null"].includes(o.op);
    if (fieldInfo.type === "number")  return !["contains"].includes(o.op);
    return true;
  });

  function setField(name) {
    const f = fields.find(x => x.name === name);
    // If switching field types, reset op + value to safe defaults
    if (f && f.type === "boolean") {
      onChange({ field: name, op: "is_true" });
    } else {
      onChange({ field: name, op: "==", value: "" });
    }
  }
  function setOp(op) {
    const newOp = OPS_BY_KEY[op] || OPS_BY_KEY["=="];
    if (newOp.valueKind === "none") {
      const { value, ...rest } = leaf;
      onChange({ ...rest, op });
    } else {
      onChange({ ...leaf, op, value: leaf.value ?? "" });
    }
  }
  function setValue(v) {
    onChange({ ...leaf, value: v });
  }

  // Value input variants
  function renderValueInput() {
    if (valueKind === "none") {
      return <div style={{ fontSize: 11, color: C.muted, alignSelf: "center", padding: "0 6px" }}>(no value needed)</div>;
    }
    if (fieldInfo.knownValues?.length > 0 && valueKind !== "list") {
      // Enum-style picker — but allow custom typing via datalist
      return (
        <>
          <input
            style={{ ...inputStyle, fontSize: 12 }}
            list={`vals-${fieldName}`}
            value={leaf.value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            placeholder="value"
          />
          <datalist id={`vals-${fieldName}`}>
            {fieldInfo.knownValues.map(v => (
              <option key={v} value={v}>
                {fieldName === "tier" && tierLabels[v] ? `${v} — ${tierLabels[v]}` : v}
              </option>
            ))}
          </datalist>
        </>
      );
    }
    if (valueKind === "number") {
      return (
        <input
          style={{ ...inputStyle, fontSize: 12 }}
          type="number"
          step="any"
          value={leaf.value ?? ""}
          onChange={(e) => setValue(e.target.value === "" ? "" : Number(e.target.value))}
          placeholder="number"
        />
      );
    }
    if (valueKind === "list") {
      return (
        <input
          style={{ ...inputStyle, fontSize: 12 }}
          value={Array.isArray(leaf.value) ? leaf.value.join(", ") : (leaf.value ?? "")}
          onChange={(e) => setValue(e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
          placeholder="value1, value2, value3"
        />
      );
    }
    return (
      <input
        style={{ ...inputStyle, fontSize: 12 }}
        value={leaf.value ?? ""}
        onChange={(e) => setValue(e.target.value)}
        placeholder="value"
      />
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.2fr) auto", gap: 6, alignItems: "center" }}>
      <select style={{ ...inputStyle, fontSize: 12 }} value={fieldName} onChange={(e) => setField(e.target.value)}>
        <option value="">(pick a field)</option>
        {fields.map(f => (
          <option key={f.name} value={f.name}>{f.label}</option>
        ))}
      </select>
      <select style={{ ...inputStyle, fontSize: 12 }} value={leaf.op || "=="} onChange={(e) => setOp(e.target.value)}>
        {validOps.map(o => (
          <option key={o.op} value={o.op}>{o.label}</option>
        ))}
      </select>
      {renderValueInput()}
      <button onClick={onRemove} style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }} title="Remove this condition">×</button>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Top-level router
// ═════════════════════════════════════════════════════════════════════════════

export default function QuoteTemplates() {
  const { id } = useParams();
  return id ? <TemplateDetail id={id} /> : <TemplatesList />;
}

// ═════════════════════════════════════════════════════════════════════════════
// CRAWLER SUGGESTIONS INBOX — what the autonomous crawler learned from won vs
// lost quotes (distinct from the Save&Learn pattern inbox on the detail page)
// ═════════════════════════════════════════════════════════════════════════════
function CrawlerSuggestionsInbox() {
  const [suggestions, setSuggestions] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [crawling, setCrawling] = useState(false);
  const [msg, setMsg]         = useState("");

  const load = useCallback(async () => {
    try { const j = await call("listQuoteSuggestions", { status: "pending" }); setSuggestions(j.suggestions || []); }
    catch (e) { setMsg("Couldn't load suggestions: " + e.message); setSuggestions([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function review(id, action) {
    setBusy(true);
    try {
      const j = await call("reviewQuoteSuggestion", { id, decision: action });
      setSuggestions(s => s.filter(x => x.id !== id));
      if (action === "approve") setMsg("✓ " + (j.message || (j.applied ? "Applied to the engine." : "Approved.")));
      else if (action === "flag_for_build") setMsg("✓ Flagged for the developer to build.");
      else if (action === "dismiss") setMsg("Dismissed.");
    } catch (e) { setMsg("Action failed: " + e.message); }
    finally { setBusy(false); }
  }
  async function runCrawl() {
    setCrawling(true); setMsg("");
    try { const j = await call("crawlQuotePatterns", { family: "generator_install" }); setMsg(`Crawled ${j.analysed?.won} won / ${j.analysed?.lost} lost → ${j.suggestions} new suggestion(s).`); await load(); }
    catch (e) { setMsg("Crawl failed: " + e.message); }
    finally { setCrawling(false); }
  }

  if (suggestions === null) return null;
  return (
    <div style={{ ...cardStyle, marginBottom: 20, border: `1.5px solid rgba(165,206,58,.35)` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>💡 Suggestions Inbox</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>What the auto-agent learned from your won vs lost Jobber quotes. Approve to act on it, Flag-to-build to queue it for the dev, or Dismiss.</div>
        </div>
        <button style={btn("ghost")} onClick={runCrawl} disabled={crawling}>{crawling ? "Crawling…" : "↻ Run crawl now"}</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{msg}</div>}
      {suggestions.length === 0 && <div style={{ color: C.muted, fontSize: 13, padding: "14px 0" }}>No pending suggestions. The crawler runs every couple days — or hit “Run crawl now”.</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {suggestions.map(s => (
          <div key={s.id} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: s.confidence === "high" ? C.good : s.confidence === "medium" ? C.warn : C.muted, border: "1px solid currentColor", borderRadius: 4, padding: "1px 5px" }}>{s.confidence}</span>
              <span style={{ fontSize: 9, color: C.muted, textTransform: "uppercase" }}>{s.kind}</span>
              {s.family && <span style={{ fontSize: 9, color: C.muted }}>· {prettifyFamily(s.family)}</span>}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginTop: 4 }}>{s.title}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>{s.detail}</div>
            {s.evidence?.summary && <div style={{ fontSize: 11, color: C.muted, marginTop: 4, fontStyle: "italic" }}>📊 {s.evidence.summary}</div>}
            <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button style={btn("ghost")} disabled={busy} onClick={() => review(s.id, "dismiss")}>Dismiss</button>
              <button style={btn("ghost")} disabled={busy} onClick={() => review(s.id, "flag_for_build")} title="Queue for the developer to build into the engine">Flag to build</button>
              <button style={btn("primary")} disabled={busy} onClick={() => review(s.id, "approve")}>Approve</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// What the price matcher learned from your corrections.
//
// Every time you pick a different part than the matcher chose, that gets
// recorded. When the same correction shows up on two separate quotes on two
// separate days, the nightly job proposes teaching it — and this is where you
// say yes. Approving replays the alias through the matcher first: if it does
// not actually reach the product you kept picking, it refuses to activate
// rather than pretending it worked.
// ─────────────────────────────────────────────────────────────────────────────
function MatchLearningInbox() {
  const [aliases, setAliases] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [showActive, setShowActive] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch("/api/match-learn?action=list");
      const j = await r.json();
      setAliases(j.aliases || []);
    } catch (e) { setMsg("Couldn't load: " + e.message); setAliases([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function review(id, decision) {
    setBusy(true); setMsg("");
    try {
      const r = await apiFetch("/api/match-learn", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "review", id, decision }),
      });
      const j = await r.json();
      if (j.ok === false) { setMsg("⚠ " + j.error); }
      else {
        setMsg(decision === "approve" ? "✓ Taught it — future quotes will find this part." : decision === "reject" ? "Rejected — it won't be suggested again." : "Retired.");
        await load();
      }
    } catch (e) { setMsg("Failed: " + e.message); }
    finally { setBusy(false); }
  }
  async function runNow() {
    setBusy(true); setMsg("");
    try {
      const r = await apiFetch("/api/match-learn", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "propose" }),
      });
      const j = await r.json();
      setMsg(j.ok === false ? "⚠ " + j.error : `Looked at ${j.corrections || 0} correction(s) → ${j.proposed || 0} new suggestion(s).`);
      await load();
    } catch (e) { setMsg("Failed: " + e.message); }
    finally { setBusy(false); }
  }

  if (aliases === null) return null;
  const proposed = aliases.filter(a => a.status === "proposed");
  const active = aliases.filter(a => a.status === "active");
  const shown = showActive ? active : proposed;

  return (
    <div style={{ ...cardStyle, marginBottom: 20, border: `1.5px solid rgba(165,206,58,.35)` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>🎯 What the matcher learned</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            When you pick a different part than the price matcher chose, that gets recorded. Correct the same
            thing on two quotes and it shows up here to teach. Teaching only makes the right part findable —
            every price check still applies, so a bad suggestion can't force a wrong price.
          </div>
        </div>
        <button style={btn("ghost")} onClick={runNow} disabled={busy}>{busy ? "Working…" : "↻ Check now"}</button>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <button style={btn(showActive ? "ghost" : "primary")} onClick={() => setShowActive(false)}>To review ({proposed.length})</button>
        <button style={btn(showActive ? "primary" : "ghost")} onClick={() => setShowActive(true)}>Already taught ({active.length})</button>
      </div>

      {msg && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{msg}</div>}

      {shown.length === 0 && (
        <div style={{ color: C.muted, fontSize: 13, padding: "14px 0" }}>
          {showActive
            ? "Nothing taught yet."
            : "Nothing to review. Corrections are collected as you work; the check runs nightly."}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {shown.map(a => (
          <div key={a.id} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
              When a quote says <b style={{ color: C.accent }}>“{a.phrase}”</b>, use{" "}
              <b>{a.catalogue_description || a.supplier_sku || "the part you picked"}</b>
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              You picked this on {a.evidence_count} quote{a.evidence_count === 1 ? "" : "s"}
              {a.evidence?.days?.length ? ` across ${a.evidence.days.length} day${a.evidence.days.length === 1 ? "" : "s"}` : ""}
              {a.evidence?.ai_guess_fixes ? ` · ${a.evidence.ai_guess_fixes} of them were red “AI guess” lines` : ""}
              {a.supplier_sku ? ` · ${a.supplier_sku}` : ""}
            </div>
            {a.status === "proposed" && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button style={btn("ghost")} disabled={busy} onClick={() => review(a.id, "reject")}>Not this</button>
                <button style={btn("primary")} disabled={busy} onClick={() => review(a.id, "approve")}>Teach it</button>
              </div>
            )}
            {a.status === "active" && (
              <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end", alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.good }}>✓ in use{a.approved_by ? ` · ${a.approved_by}` : ""}</span>
                <button style={btn("ghost")} disabled={busy} onClick={() => review(a.id, "retire")}>Stop using</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// TEACHING LOOP — how far off the drafts are, and what to teach next
// ═════════════════════════════════════════════════════════════════════════════

// "How much did a human have to change the AI's draft before sending it?" —
// measured on every push (quote_edit_diffs), per service family. This is the
// number that decides when a family can be trusted to a cheaper model tier,
// and eventually to a tech in the field with no review step.
function AccuracyScorecard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/quote-teach?action=scorecard");
        const j = await r.json();
        setData(j.ok === false ? { error: j.error } : j);
      } catch (e) { setData({ error: e.message }); }
    })();
  }, []);

  if (!data || data.error) return null;
  const fams = Object.entries(data.families || {}).sort((a, b) => b[1].sample - a[1].sample);
  if (!fams.length && !(data.overall?.sample > 0)) return null;

  const pct = (v) => (v == null ? "—" : `${v}%`);
  const colourFor = (v) => (v == null ? C.muted : v <= 2 ? C.good : v <= 5 ? C.accent : C.warn);

  return (
    <div style={{ ...cardStyle, marginBottom: 20, border: `1.5px solid rgba(165,206,58,.35)` }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>📊 How close the drafts are</div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
        Every time a quote is pushed to Jobber, ElecBid compares what the AI drafted to what you actually sent.
        This is that gap. The goal is 2% or less — at that point the draft is good enough to send without a review.
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
        {fams.map(([fam, s]) => (
          <div key={fam} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, minWidth: 210, flex: "1 1 210px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{fam.replace(/_/g, " ")}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: colourFor(s.median_abs_delta_pct), lineHeight: 1.2, marginTop: 4 }}>
              {pct(s.median_abs_delta_pct)}
            </div>
            <div style={{ fontSize: 10, color: C.muted }}>
              typical change · {s.sample} quote{s.sample === 1 ? "" : "s"} · {pct(s.within_5_pct)} within 5%
            </div>
            <div style={{ fontSize: 10, color: /READY/.test(s.promotion_gate) ? C.good : C.muted, marginTop: 6 }}>
              {/READY/.test(s.promotion_gate) ? "✓ trusted — ready to run cheaper" : s.promotion_gate}
            </div>
            {s.top_edit_classes?.length > 0 && (
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>most edited: {s.top_edit_classes.slice(0, 2).join(", ")}</div>
            )}
          </div>
        ))}
      </div>
      {data.overall?.sample > 0 && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>
          Across everything: typical change {pct(data.overall.median_abs_delta_pct)} over {data.overall.sample} quote
          {data.overall.sample === 1 ? "" : "s"} ({pct(data.overall.within_2_pct)} already inside the 2% target).
        </div>
      )}
    </div>
  );
}

// Lessons distilled from the edits you make before sending. Approving one
// writes a real rule the quote engine reads on every future draft.
function QuoteLessonsInbox() {
  const [lessons, setLessons] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await apiFetch("/api/quote-teach?action=list&status=proposed");
      const j = await r.json();
      setLessons(j.lessons || []);
    } catch (e) { setMsg("Couldn't load: " + e.message); setLessons([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function review(id, decision) {
    setBusy(true); setMsg("");
    try {
      const r = await apiFetch("/api/quote-teach", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "review", id, decision }),
      });
      const j = await r.json();
      if (j.ok === false) setMsg("⚠ " + j.error);
      else { setMsg(decision === "approve" ? "✓ Taught it — future drafts will follow this." : "Rejected — it won't come back."); await load(); }
    } catch (e) { setMsg("Failed: " + e.message); }
    finally { setBusy(false); }
  }
  async function runNow() {
    setBusy(true); setMsg("");
    try {
      const r = await apiFetch("/api/quote-teach", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "distill" }),
      });
      const j = await r.json();
      setMsg(j.ok === false ? "⚠ " + j.error : `Looked at ${j.diffs_scanned || 0} quote(s) → ${j.created || 0} new lesson(s).`);
      await load();
    } catch (e) { setMsg("Failed: " + e.message); }
    finally { setBusy(false); }
  }

  if (lessons === null) return null;

  return (
    <div style={{ ...cardStyle, marginBottom: 20, border: `1.5px solid rgba(165,206,58,.35)` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.accent }}>🎓 What the quote engine learned</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Every edit you make before sending a quote is recorded. Change the same thing on two quotes across two
            days and it shows up here. Nothing changes until you approve it.
          </div>
        </div>
        <button style={btn("ghost")} onClick={runNow} disabled={busy}>{busy ? "Working…" : "↻ Check now"}</button>
      </div>

      {msg && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{msg}</div>}

      {lessons.length === 0 && (
        <div style={{ color: C.muted, fontSize: 13, padding: "14px 0" }}>
          Nothing to review. Edits are collected as you quote; the check runs nightly.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
        {lessons.map(l => (
          <div key={l.id} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5, fontWeight: 600 }}>{l.title}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{l.detail}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
              {l.family !== "_all" ? `${l.family.replace(/_/g, " ")} · ` : ""}seen on {l.evidence_count} quote{l.evidence_count === 1 ? "" : "s"}
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button style={btn("ghost")} disabled={busy} onClick={() => review(l.id, "reject")}>Not a rule</button>
              <button style={btn("primary")} disabled={busy} onClick={() => review(l.id, "approve")}>Teach it</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LIST PAGE
// ═════════════════════════════════════════════════════════════════════════════

function TemplatesList() {
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const j = await call("listTemplates", { include_inactive: showInactive });
        setPlaybooks(j.playbooks || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [showInactive]);

  // Group by service_family for readable browsing
  const grouped = useMemo(() => {
    const out = {};
    for (const p of playbooks) {
      const k = p.service_family || "other";
      (out[k] = out[k] || []).push(p);
    }
    return out;
  }, [playbooks]);

  return (
    <div style={pageStyle}>
      <Header
        title="Quote Templates"
        subtitle="The decision rules the quote engine reads when drafting a quote. Edit a template here, every future quote of that service family reflects the change."
      />

      <AccuracyScorecard />
      <QuoteLessonsInbox />
      <MatchLearningInbox />
      <CrawlerSuggestionsInbox />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
          Show inactive versions
        </label>
        <button style={btn("primary", true)} disabled title="Coming in Phase B4">+ New Template (coming soon)</button>
      </div>

      {loading && <div style={{ color: C.muted, padding: 24, textAlign: "center" }}>Loading…</div>}
      {error && <div style={{ color: C.danger, padding: 16 }}>Error: {error}</div>}

      {!loading && !error && playbooks.length === 0 && (
        <div style={{ ...cardStyle, textAlign: "center", padding: 40, color: C.muted }}>
          No templates yet.
        </div>
      )}

      {!loading && !error && Object.keys(grouped).sort().map(family => (
        <div key={family} style={{ marginBottom: 24 }}>
          <div style={sectionHeadStyle}>{prettifyFamily(family)}</div>
          <div style={{ display: "grid", gap: 10 }}>
            {grouped[family].map(p => (
              <TemplateRow key={p.id} pb={p} onOpen={() => navigate(`/quote-templates/${p.id}`)} />
            ))}
          </div>
        </div>
      ))}

      <FuturePackagesHint />
    </div>
  );
}

function TemplateRow({ pb, onOpen }) {
  const stats = pb.node_stats || { total: 0, by_type: {} };
  return (
    <button
      onClick={onOpen}
      style={{
        ...cardStyle,
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
        opacity: pb.is_active ? 1 : 0.55,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
            {pb.name}
            <span style={{ marginLeft: 8, fontSize: 11, color: C.muted, fontWeight: 500 }}>{pb.version}</span>
          </div>
          {pb.description && (
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>{pb.description}</div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Pill color={pb.is_active ? C.accent : C.muted}>{pb.is_active ? "Active" : "Inactive"}</Pill>
            {pb.is_template && <Pill color="#7dd3fc">Template</Pill>}
            <Pill color={C.muted}>{stats.total} nodes</Pill>
            {stats.by_type?.material && <Pill color="#7dd3fc">{stats.by_type.material} materials</Pill>}
            {stats.by_type?.scope_item && <Pill color={C.text}>{stats.by_type.scope_item} scope</Pill>}
            {stats.by_type?.hard_fail && <Pill color={C.warn}>{stats.by_type.hard_fail} QC checks</Pill>}
          </div>
        </div>
        <div style={{ fontSize: 22, color: C.muted, alignSelf: "center" }}>›</div>
      </div>
    </button>
  );
}

function FuturePackagesHint() {
  const future = [
    "EV Charger", "Service Upgrade", "Panel Replacement", "Hot Tub",
    "Mini Split / Heat Pump", "Solar PV", "Battery Backup", "Sauna",
    "Pumps / Controls", "Refrigeration", "Lighting", "Commercial",
  ];
  return (
    <div style={{ ...cardStyle, marginTop: 16, background: C.surface2 }}>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
        Future templates you'll be able to build once the editor (Phase B4) is live:
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {future.map(f => <Pill key={f} color={C.muted}>{f}</Pill>)}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// DETAIL PAGE
// ═════════════════════════════════════════════════════════════════════════════

function TemplateDetail({ id }) {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Live preview state — sample inputs the user can tweak
  const [previewInputs, setPreviewInputs] = useState({
    panel_amps:        100,
    load_calc_amps:    78,
    feed_length_m:     15,
    trenching:         "none",
    hot_tub_on_backup: false,
    service_upgrade_combo:  false,
    panel_replacement_combo:false,
    client_owns_generator:  false,
    competitive_pricing:    false,
  });
  const [preview,        setPreview]        = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState(null);

  // Edit mode state (Phase B4)
  const [editingNodeId, setEditingNodeId] = useState(null);    // node currently being edited
  const [unsavedNodes,  setUnsavedNodes]  = useState({});       // nodeId → modified config
  const [savingNode,    setSavingNode]    = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [savingVersion,    setSavingVersion]    = useState(false);
  const [versionNote,      setVersionNote]      = useState("");
  // Recipe drawer: when set to a node_type string, shows the recipe picker
  // for that type. null = drawer closed.
  const [recipeDrawerFor, setRecipeDrawerFor] = useState(null);

  // Historical quotes for this family
  const [history,        setHistory]        = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError,   setHistoryError]   = useState(null);

  // Suggestions inbox (learning loop, B5)
  const [suggestions,         setSuggestions]         = useState([]);
  const [suggestionsLoading,  setSuggestionsLoading]  = useState(false);
  const [analyzingPatterns,   setAnalyzingPatterns]   = useState(false);
  const [analyzeMessage,      setAnalyzeMessage]      = useState(null);

  const reloadSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    try {
      const j = await call("listSuggestions", { playbook_id: id, status: "pending" });
      if (j.ok) setSuggestions(j.suggestions || []);
    } catch (e) {
      // Non-fatal — suggestions just don't show
      console.warn("listSuggestions failed:", e.message);
    } finally {
      setSuggestionsLoading(false);
    }
  }, [id]);
  useEffect(() => { reloadSuggestions(); }, [reloadSuggestions]);

  // Scan BOTH sources: marked-for-learning quotes AND historical jb_jobs.
  // Each runs independently and we combine the messages into one summary.
  const handleAnalyzePatterns = useCallback(async () => {
    setAnalyzingPatterns(true);
    setAnalyzeMessage(null);
    try {
      const [markedRes, historyRes] = await Promise.all([
        call("analyzePatterns", { playbook_id: id }),
        call("analyzeHistory",  { playbook_id: id }),
      ]);
      const markedCreated  = markedRes?.ok ? (markedRes.suggestions_created || 0) : 0;
      const historyCreated = historyRes?.ok ? (historyRes.suggestions_created || 0) : 0;
      const markedEvidence  = markedRes?.evidence_count || 0;
      const historyEvidence = historyRes?.evidence_count || 0;
      const parts = [];
      if (markedRes?.ok) {
        if (markedCreated > 0) parts.push(`${markedCreated} from ${markedEvidence} Save & Learn'd quote(s)`);
        else if (markedEvidence > 0) parts.push(`scanned ${markedEvidence} Save & Learn'd quote(s) — no new patterns yet`);
      } else if (markedRes?.error) {
        parts.push(`marked-quotes scan failed: ${markedRes.error}`);
      }
      if (historyRes?.ok) {
        if (historyCreated > 0) parts.push(`${historyCreated} from ${historyEvidence} historical won job(s)`);
        else if (historyEvidence > 0) parts.push(`scanned ${historyEvidence} won job(s) — playbook already covers them`);
      } else if (historyRes?.error) {
        parts.push(`history scan failed: ${historyRes.error}`);
      }
      const total = markedCreated + historyCreated;
      setAnalyzeMessage(
        total > 0
          ? `✨ ${total} new suggestion(s): ${parts.join(" · ")}.`
          : (parts.length ? parts.join(" · ") + "." : "Nothing to scan yet.")
      );
      await reloadSuggestions();
    } catch (e) {
      setAnalyzeMessage(`Error: ${e.message}`);
    } finally {
      setAnalyzingPatterns(false);
    }
  }, [id, reloadSuggestions]);

  const handleSuggestionAction = useCallback(async (suggestionId, status, snoozeDays) => {
    try {
      const body = { suggestion_id: suggestionId, status };
      if (status === "snoozed" && snoozeDays) body.snooze_days = snoozeDays;
      const j = await call("updateSuggestion", body);
      if (!j.ok) throw new Error(j.error || "Update failed");
      // Optimistic remove from the list
      setSuggestions(s => s.filter(x => x.id !== suggestionId));
    } catch (e) {
      alert("Failed: " + e.message);
    }
  }, []);

  // Load template
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const j = await call("getTemplate", { id });
      if (!j.ok) throw new Error(j.error || "not found");
      setData({ playbook: j.playbook, nodes: j.nodes, versions: j.versions });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  // Approve a suggestion → server applies it to the playbook + marks approved.
  // We refresh both suggestions AND the node tree (since a new node landed).
  // Defined here (after reload) so the dep array is clean.
  const handleSuggestionApprove = useCallback(async (suggestionId) => {
    try {
      const j = await call("approveSuggestion", { suggestion_id: suggestionId });
      if (!j.ok) throw new Error(j.error || "Approve failed");
      // Optimistic remove from the inbox + refresh the playbook to show the new node
      setSuggestions(s => s.filter(x => x.id !== suggestionId));
      await reload();
    } catch (e) {
      alert("Approve failed: " + e.message);
    }
  }, [reload]);

  // Fetch historical quotes for this family once the playbook loads
  useEffect(() => {
    if (!data?.playbook?.service_family) return;
    let cancelled = false;
    (async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const j = await call("getFamilyHistory", { service_family: data.playbook.service_family, limit: 100 });
        if (!cancelled) setHistory(j);
      } catch (e) {
        if (!cancelled) setHistoryError(e.message);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data?.playbook?.service_family]);

  // Save an edited node back to the DB
  const handleSaveNode = useCallback(async (nodeId, patch) => {
    setSavingNode(true);
    try {
      // patch.config gets the merged config; other patchable fields can also flow
      const res = await call("updateNode", { node_id: nodeId, ...patch });
      if (!res.ok) throw new Error(res.error || "Save failed");
      // Optimistic update of local state so the UI reflects immediately
      setData(d => ({
        ...d,
        nodes: d.nodes.map(n => n.id === nodeId ? { ...n, ...patch } : n),
      }));
      // Clear unsaved marker
      setUnsavedNodes(u => { const x = { ...u }; delete x[nodeId]; return x; });
      setEditingNodeId(null);
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSavingNode(false);
    }
  }, []);

  // Delete a node (soft-delete via API)
  const handleDeleteNode = useCallback(async (nodeId) => {
    setSavingNode(true);
    try {
      const res = await call("deleteNode", { node_id: nodeId });
      if (!res.ok) throw new Error(res.error || "Delete failed");
      setData(d => ({ ...d, nodes: d.nodes.map(n => n.id === nodeId ? { ...n, active: false } : n) }));
    } catch (e) {
      alert("Delete failed: " + e.message);
    } finally {
      setSavingNode(false);
    }
  }, []);

  // Create a new node. If `recipe` is provided, its config wins. Otherwise we
  // fall back to the type-specific blank defaults. The recipe drawer flow
  // closes the drawer on success so the new node lands in edit mode.
  const handleCreateNode = useCallback(async (nodeType, recipe = null) => {
    setSavingNode(true);
    try {
      const defaults = {
        scope_item:      { text_template: "New scope item" },
        note:            { text_template: "New note" },
        exclusion:       { text_template: "Not included: …" },
        hard_fail:       { code: "new_check", severity: "warn", message_template: "New check", condition: true },
        material:        { sku_template: "NEW_ITEM", description_template: "New material", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 },
        material_picker: { selection_rules: [{ default: true, set: { sku_template: "NEW_ITEM", description_template: "Default option", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } }] },
        labour_rule:     { base_hours: 8, adjustments: [], rate_options: [{ name: "standard", rate: 280, default: true }] },
      };
      const config = recipe?.config || defaults[nodeType] || {};
      const res = await call("createNode", {
        playbook_id: id,
        node_type:   nodeType,
        config,
      });
      if (!res.ok) throw new Error(res.error || "Create failed");
      // Optimistic add
      setData(d => ({ ...d, nodes: [...d.nodes, res.node] }));
      // Auto-open the new node in edit mode
      setEditingNodeId(res.node.id);
      // Close the recipe drawer if it was open
      setRecipeDrawerFor(null);
    } catch (e) {
      alert("Create failed: " + e.message);
    } finally {
      setSavingNode(false);
    }
  }, [id]);

  // Roll back to a specific version
  const handleRollback = useCallback(async (versionId, versionLabel) => {
    if (!window.confirm(`Roll back to ${versionLabel}? Current state will be saved as a pre-rollback snapshot first.`)) return;
    setSavingNode(true);
    try {
      const res = await call("rollbackToVersion", { version_id: versionId });
      if (!res.ok) throw new Error(res.error || "Rollback failed");
      await reload();
    } catch (e) {
      alert("Rollback failed: " + e.message);
    } finally {
      setSavingNode(false);
    }
  }, [reload]);

  // Save a new version snapshot
  const handleSaveVersion = useCallback(async () => {
    if (!versionNote.trim()) return;
    setSavingVersion(true);
    try {
      const res = await call("saveTemplateVersion", { playbook_id: id, change_note: versionNote.trim() });
      if (!res.ok) throw new Error(res.error || "Failed");
      setShowVersionModal(false);
      setVersionNote("");
      await reload();
    } catch (e) {
      alert("Save version failed: " + e.message);
    } finally {
      setSavingVersion(false);
    }
  }, [id, versionNote, reload]);

  // Re-run preview whenever inputs change (debounced)
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const j = await call("previewTemplate", { id, inputs: previewInputs });
        setPreview(j.draft);
      } catch (e) {
        setPreviewError(e.message);
      } finally {
        setPreviewLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [previewInputs, id, data]);

  if (loading) {
    return (
      <div style={pageStyle}>
        <button style={btn("ghost")} onClick={() => navigate("/quote-templates")}>‹ All templates</button>
        <div style={{ color: C.muted, padding: 24, textAlign: "center" }}>Loading…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={pageStyle}>
        <button style={btn("ghost")} onClick={() => navigate("/quote-templates")}>‹ All templates</button>
        <div style={{ color: C.danger, padding: 16 }}>Error: {error || "not found"}</div>
      </div>
    );
  }

  const { playbook, nodes, versions } = data;

  return (
    <div style={pageStyle}>
      <button style={btn("ghost")} onClick={() => navigate("/quote-templates")}>‹ All templates</button>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "16px 0", flexWrap: "wrap", gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 26, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.03em" }}>
            {playbook.name}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <span>{playbook.version}</span>
            <span>{prettifyFamily(playbook.service_family)}</span>
            <span>{nodes.length} nodes</span>
            {playbook.is_template && <span style={{ color: "#7dd3fc" }}>Community template</span>}
            <span style={{ color: playbook.is_active ? C.accent : C.muted }}>
              {playbook.is_active ? "● Active" : "Inactive"}
            </span>
          </div>
          {playbook.description && (
            <div style={{ fontSize: 13, color: C.muted, marginTop: 8, maxWidth: 720 }}>{playbook.description}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {Object.keys(unsavedNodes).length > 0 && (
            <span style={{ fontSize: 11, color: C.warn, alignSelf: "center" }}>
              {Object.keys(unsavedNodes).length} unsaved edit(s)
            </span>
          )}
          <button style={btn("primary")} onClick={() => setShowVersionModal(true)} title="Snapshot current state as a new version">
            Save as new version
          </button>
          <button style={btn(undefined, true)} disabled title="Phase B5 — multi-tenant fork">Fork (B5)</button>
        </div>
      </div>

      {/* Edit-mode banner */}
      <div style={{
        background: "rgba(165,206,58,0.08)",
        border: `1px solid rgba(165,206,58,0.3)`,
        borderRadius: 10,
        padding: "10px 14px",
        fontSize: 12,
        color: C.text,
        marginBottom: 16,
      }}>
        ✏️ <strong>Editing enabled.</strong> Click any material node's <code style={{ background: C.surface2, padding: "2px 6px", borderRadius: 4 }}>Edit</code> button to change its description, price, qty, markup, or supplier lookup patterns.
        Edits save individually. Click <em>Save as new version</em> at the top to snapshot the whole template + a change note.
      </div>

      {/* Suggestions inbox — patterns the learning engine detected from
          Save & Learn'd quotes. Read-only for Phase B5; Phase B6 adds Approve. */}
      <SuggestionsInbox
        suggestions={suggestions}
        loading={suggestionsLoading}
        analyzing={analyzingPatterns}
        analyzeMessage={analyzeMessage}
        onAnalyze={handleAnalyzePatterns}
        onAction={handleSuggestionAction}
        onApprove={handleSuggestionApprove}
      />

      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 16,
      }}>
        {/* Left: the tree */}
        <div>
          <SectionTitle>Decision Tree</SectionTitle>
          <NodeTree
            nodes={nodes}
            preview={preview}
            editingNodeId={editingNodeId}
            onEditStart={setEditingNodeId}
            onEditCancel={() => setEditingNodeId(null)}
            onSave={handleSaveNode}
            onDelete={handleDeleteNode}
            onCreate={(t) => setRecipeDrawerFor(t)}
            saving={savingNode}
          />

          {/* Rules library + pitfalls (skeleton meta) */}
          <RulesLibrary playbook={playbook} />

          {/* Historical quotes from jb_quotes + jb_jobs */}
          <HistoricalQuotes history={history} loading={historyLoading} error={historyError} />

          {versions && versions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionTitle>Versions</SectionTitle>
              <div style={cardStyle}>
                {versions.map((v, i) => (
                  <div key={v.id} style={{
                    padding: "8px 0",
                    borderBottom: `1px solid ${C.border}`,
                    display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center",
                  }}>
                    <div style={{ fontSize: 12, minWidth: 0, flex: 1 }}>
                      <div style={{ color: C.text, fontWeight: 600 }}>{v.version}{i === 0 && <span style={{ color: C.accent, fontSize: 10, marginLeft: 6 }}>● current</span>}</div>
                      {v.change_note && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{v.change_note.slice(0, 100)}</div>}
                      <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{new Date(v.created_at).toLocaleString()}</div>
                    </div>
                    {i !== 0 && (
                      <button
                        onClick={() => handleRollback(v.id, v.version)}
                        style={{ ...btn("ghost"), padding: "4px 10px", fontSize: 11 }}
                        disabled={savingNode}
                        title="Restore this version. Current state will be saved as a snapshot first."
                      >
                        Roll back
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: preview pane */}
        <div>
          <SectionTitle>Live Preview</SectionTitle>
          <PreviewInputsCard inputs={previewInputs} onChange={setPreviewInputs} />
          <PreviewResultCard preview={preview} loading={previewLoading} error={previewError} />
        </div>
      </div>

      {/* Rule Recipes drawer */}
      {recipeDrawerFor && (
        <RecipeDrawer
          nodeType={recipeDrawerFor}
          onClose={() => setRecipeDrawerFor(null)}
          onPick={(recipe) => handleCreateNode(recipeDrawerFor, recipe)}
          saving={savingNode}
        />
      )}

      {/* Save Version modal */}
      {showVersionModal && (
        <div
          onClick={() => !savingVersion && setShowVersionModal(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 480, padding: 20 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Save current template as new version</div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
              Snapshots all current nodes. You can roll back to this version anytime via the Versions panel.
            </div>
            <label style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4, display: "block" }}>
              What changed?
            </label>
            <textarea
              value={versionNote}
              onChange={e => setVersionNote(e.target.value)}
              placeholder="e.g., Increased misc allowance to $750 for whole-home"
              rows={3}
              style={{ ...inputStyle, resize: "vertical", marginBottom: 12 }}
              autoFocus
            />
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={btn("ghost")} onClick={() => setShowVersionModal(false)} disabled={savingVersion}>Cancel</button>
              <button style={btn("primary")} onClick={handleSaveVersion} disabled={savingVersion || !versionNote.trim()}>
                {savingVersion ? "Saving…" : "Save version"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tree view of nodes (grouped by type) ────────────────────────────────────
function NodeTree({ nodes, preview, editingNodeId, onEditStart, onEditCancel, onSave, onDelete, onCreate, saving }) {
  // Derive the field schema once per render so every editor in the tree sees
  // the same list of fields + tier labels.
  const schema = useMemo(() => collectPlaybookSchema(nodes), [nodes]);
  // Group nodes by type for browsability; keep position order inside each group
  const grouped = useMemo(() => {
    const out = {};
    for (const n of nodes) {
      (out[n.node_type] = out[n.node_type] || []).push(n);
    }
    for (const k of Object.keys(out)) out[k].sort((a, b) => (a.position || 0) - (b.position || 0));
    return out;
  }, [nodes]);

  // Build a set of node_ids whose include_if currently evaluates true so we can
  // mark "active" nodes in the live preview. Rough heuristic — we use the
  // preview's materials/scope/notes by sku/id/text.
  const activeIds = useMemo(() => {
    if (!preview) return null;
    const out = new Set();
    const matSkus = new Set((preview.materials || []).map(m => m.sku));
    const scopeText = new Set((preview.scope || []).map(s => (s.text || "").toLowerCase()));
    const noteText  = new Set((preview.notes || []).map(s => (s.text || "").toLowerCase()));
    const exclText  = new Set((preview.exclusions || []).map(s => (s.text || "").toLowerCase()));
    for (const n of nodes) {
      const c = n.config || {};
      if (n.node_type === "material" && matSkus.has(interpolateLite(c.sku_template, preview?.meta || {}))) out.add(n.id);
      if (n.node_type === "material_picker") {
        // picker fires if any of its selection_rules' sku appears
        for (const r of (c.selection_rules || [])) {
          if (r.set && matSkus.has(r.set.sku_template || r.set.sku)) { out.add(n.id); break; }
        }
      }
      if (n.node_type === "scope_item") {
        const t = interpolateLite(c.text_template || c.text || "", preview?.meta || {}).toLowerCase();
        if (scopeText.has(t)) out.add(n.id);
      }
      if (n.node_type === "note") {
        const t = interpolateLite(c.text_template || c.text || "", preview?.meta || {}).toLowerCase();
        if (noteText.has(t)) out.add(n.id);
      }
      if (n.node_type === "exclusion") {
        const t = interpolateLite(c.text_template || c.text || "", preview?.meta || {}).toLowerCase();
        if (exclText.has(t)) out.add(n.id);
      }
      if (n.node_type === "tier_classifier" || n.node_type === "labour_rule") out.add(n.id); // always run
    }
    return out;
  }, [nodes, preview]);

  // Render order — by importance to a reader skimming the tree
  const order = [
    "tier_classifier",
    "material_picker", "material",
    "labour_rule",
    "scope_item",
    "note",
    "exclusion",
    "hard_fail",
    "condition_group",
  ];

  return (
    <div>
      {order.filter(t => grouped[t]?.length).map(t => {
        const meta = NODE_TYPE_META[t] || { icon: "·", label: t };
        return (
          <div key={t} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              {meta.icon} {meta.label} <span style={{ color: C.muted, opacity: 0.7 }}>({grouped[t].length})</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {grouped[t].map(n => (
                <NodeCard
                  key={n.id}
                  node={n}
                  active={activeIds ? activeIds.has(n.id) : null}
                  editing={editingNodeId === n.id}
                  onEditStart={onEditStart}
                  onEditCancel={onEditCancel}
                  onSave={onSave}
                  onDelete={onDelete}
                  saving={saving}
                  schema={schema}
                />
              ))}
              {onCreate && ADD_NODE_TYPES.includes(t) && (
                <button
                  onClick={() => onCreate(t)}
                  disabled={saving}
                  style={{ ...cardStyle, padding: "8px 12px", textAlign: "left", cursor: "pointer", color: C.muted, fontSize: 12, fontStyle: "italic", borderStyle: "dashed" }}
                >
                  + Add {NODE_TYPE_META[t]?.label?.toLowerCase() || t.replace("_", " ")}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Node types that support "+ Add" from the section header. tier_classifier
// is one-per-playbook (don't surface a button). Everything else has at least
// one Rule Recipe defined, so clicking "+ Add" opens the recipe drawer.
const ADD_NODE_TYPES = ["material", "material_picker", "scope_item", "note", "exclusion", "labour_rule", "hard_fail"];

// ── Recipe Drawer ──────────────────────────────────────────────────────────
// Zapier-style picker: shows the rule recipes for the node_type the user is
// about to add. Each recipe is a pre-filled config they can drop in and
// customize. "Start blank" creates the type-specific default config.
function RecipeDrawer({ nodeType, onClose, onPick, saving }) {
  const recipes = RULE_RECIPES[nodeType] || [];
  const meta = NODE_TYPE_META[nodeType] || { icon: "·", label: nodeType };
  return (
    <div
      onClick={() => !saving && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24, overflowY: "auto" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 640, padding: 20, marginTop: 40 }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>
            {meta.icon} Add a {meta.label.toLowerCase()}
          </div>
          <button onClick={onClose} disabled={saving} style={{ ...btn("ghost"), padding: "4px 10px", fontSize: 13 }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
          Pick a starter recipe to pre-fill the new node, or start blank. After it lands, the inline editor opens so you can tweak it.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {recipes.map(r => (
            <button
              key={r.key}
              onClick={() => onPick(r)}
              disabled={saving}
              style={{
                ...cardStyle,
                padding: "10px 14px",
                textAlign: "left",
                cursor: saving ? "not-allowed" : "pointer",
                background: r.key === "blank" ? C.surface2 : C.surface,
                opacity: saving ? 0.5 : 1,
              }}
              onMouseEnter={(e) => { if (!saving) e.currentTarget.style.borderColor = C.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{r.label}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{r.desc}</div>
            </button>
          ))}
          {recipes.length === 0 && (
            <div style={{ ...cardStyle, color: C.muted, fontSize: 12, fontStyle: "italic" }}>
              No recipes defined for this node type yet — start with a blank config.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NodeCard({ node, active, editing, onEditStart, onEditCancel, onSave, onDelete, saving, schema }) {
  const meta = NODE_TYPE_META[node.node_type] || { icon: "·", label: node.node_type, color: C.text };
  const label = node.display_label || node.config?.label || meta.label;
  const summary = summarizeNode(node, schema);
  const isActive = active === true;
  const isInactive = active === false;
  // All node types are now inline-editable; the editor varies by type.
  const EDITABLE_TYPES = ["material","scope_item","note","exclusion","labour_rule","hard_fail","tier_classifier","material_picker"];
  const isEditable = EDITABLE_TYPES.includes(node.node_type);

  // Pick the right editor for this node type
  function renderEditor() {
    const common = { node, onCancel: onEditCancel, onSave: (patch) => onSave(node.id, patch), saving, schema };
    switch (node.node_type) {
      case "material":         return <MaterialNodeEditor   {...common} />;
      case "scope_item":
      case "note":
      case "exclusion":        return <TextNodeEditor        {...common} />;
      case "labour_rule":      return <LabourRuleEditor      {...common} />;
      case "hard_fail":        return <HardFailEditor        {...common} />;
      case "tier_classifier":  return <TierClassifierEditor  {...common} />;
      case "material_picker":  return <MaterialPickerEditor  {...common} />;
      default: return <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Editor for {node.node_type} coming soon.</div>;
    }
  }

  return (
    <div style={{
      ...cardStyle,
      padding: "10px 12px",
      borderLeft: `3px solid ${editing ? "#fbbf24" : (isActive ? C.accent : (isInactive ? C.border : meta.color))}`,
      opacity: isInactive ? 0.5 : 1,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 2 }}>
            {meta.icon} {label}
            {isActive && (
              <span style={{ marginLeft: 6, fontSize: 10, color: C.accent, fontWeight: 700, letterSpacing: 0.5 }}>● FIRED</span>
            )}
            {editing && (
              <span style={{ marginLeft: 6, fontSize: 10, color: "#fbbf24", fontWeight: 700, letterSpacing: 0.5 }}>✏️ EDITING</span>
            )}
          </div>
          {summary && !editing && (
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>{summary}</div>
          )}
        </div>
        {isEditable && !editing && (
          <div style={{ display: "flex", gap: 4 }}>
            <button onClick={() => onEditStart && onEditStart(node.id)} style={{ ...btn("ghost"), padding: "3px 8px", fontSize: 11 }}>
              Edit
            </button>
            {onDelete && (
              <button
                onClick={() => {
                  if (window.confirm(`Delete this ${node.node_type.replace("_", " ")}? (Can be restored from version history.)`)) {
                    onDelete(node.id);
                  }
                }}
                style={{ ...btn("ghost"), padding: "3px 8px", fontSize: 11, color: C.danger }}
                title="Soft-delete (recoverable via version rollback)"
              >
                ×
              </button>
            )}
          </div>
        )}
      </div>

      {editing && isEditable && renderEditor()}
    </div>
  );
}

// ── Material node inline editor ─────────────────────────────────────────────
// Lets the user change description/qty/unit/markup/PST/fallback price, plus
// edit the supplier-price lookup patterns and SKU patterns.
function MaterialNodeEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  // Local form state — starts as a snapshot of the node's config
  const [display, setDisplay] = useState(node.display_label || "");
  const [sku,     setSku]     = useState(c.sku_template || "");
  const [desc,    setDesc]    = useState(c.description_template || "");
  const [qty,     setQty]     = useState(
    typeof c.qty === "object" ? "" : String(c.qty ?? "1")
  );
  const [unit,    setUnit]    = useState(c.unit || "ea");
  const [markup,  setMarkup]  = useState(String(c.markup_pct ?? "30"));
  const [pst,     setPst]     = useState(String(c.pst_pct ?? "7"));
  const [fallback,setFallback]= useState(String(c.fallback_price ?? "0"));
  const [patterns,setPatterns]= useState((c.lookup?.patterns_template || c.lookup?.patterns || []).join("\n"));
  const [skuPats, setSkuPats] = useState((c.lookup?.sku_patterns_template || c.lookup?.sku_patterns || []).join("\n"));
  const [suppliers,setSuppliers]= useState((c.lookup?.supplier_preference || []).join(", "));
  const [minPrice, setMinPrice] = useState(c.lookup?.min_price != null ? String(c.lookup.min_price) : "");
  const [maxPrice, setMaxPrice] = useState(c.lookup?.max_price != null ? String(c.lookup.max_price) : "");
  const [includeIf, setIncludeIf] = useState(c.include_if ?? null);

  function handleSave() {
    // Build the new config — preserve any other fields we don't edit
    const newConfig = {
      ...c,
      sku_template:         sku || undefined,
      description_template: desc || undefined,
      qty:                  typeof c.qty === "object" ? c.qty : (qty === "" ? 1 : Number(qty)),
      unit,
      markup_pct:           Number(markup) || 0,
      pst_pct:              Number(pst) || 0,
      fallback_price:       Number(fallback) || 0,
      include_if:           includeIf || undefined,
    };
    const newLookup = { ...(c.lookup || {}) };
    const dp = patterns.split("\n").map(s => s.trim()).filter(Boolean);
    const sp = skuPats.split("\n").map(s => s.trim()).filter(Boolean);
    const sup = suppliers.split(",").map(s => s.trim()).filter(Boolean);
    // Prefer patterns_template / sku_patterns_template since the executor will
    // interpolate them. If the original used the plain key, preserve that.
    const usingTemplate = c.lookup?.patterns_template != null || (c.lookup?.patterns == null);
    if (usingTemplate) newLookup.patterns_template = dp; else newLookup.patterns = dp;
    const usingSkuTemplate = c.lookup?.sku_patterns_template != null || (c.lookup?.sku_patterns == null);
    if (usingSkuTemplate) newLookup.sku_patterns_template = sp; else newLookup.sku_patterns = sp;
    newLookup.supplier_preference = sup;
    if (minPrice !== "") newLookup.min_price = Number(minPrice); else delete newLookup.min_price;
    if (maxPrice !== "") newLookup.max_price = Number(maxPrice); else delete newLookup.max_price;
    if (dp.length || sp.length || sup.length || minPrice !== "" || maxPrice !== "") {
      newConfig.lookup = newLookup;
    } else if (c.lookup) {
      newConfig.lookup = newLookup; // keep empty lookup so editor remembers user cleared it
    }
    onSave({ display_label: display || null, config: newConfig });
  }

  return (
    <div style={{ marginTop: 10, padding: 10, background: C.surface2, borderRadius: 8, border: `1px solid ${C.border}` }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <Field label="Display label">
          <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} />
        </Field>
        <Field label="SKU template">
          <input style={inputStyle} value={sku} onChange={e => setSku(e.target.value)} />
        </Field>
      </div>
      <Field label="Description template">
        <input style={inputStyle} value={desc} onChange={e => setDesc(e.target.value)} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
        <Field label="Qty">
          <input style={inputStyle} value={qty} onChange={e => setQty(e.target.value)} disabled={typeof c.qty === "object"} />
          {typeof c.qty === "object" && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>(from {c.qty.field})</div>}
        </Field>
        <Field label="Unit"><input style={inputStyle} value={unit} onChange={e => setUnit(e.target.value)} /></Field>
        <Field label="Markup %"><input style={inputStyle} type="number" value={markup} onChange={e => setMarkup(e.target.value)} /></Field>
        <Field label="PST %"><input style={inputStyle} type="number" value={pst} onChange={e => setPst(e.target.value)} /></Field>
        <Field label="Fallback $"><input style={inputStyle} type="number" step="0.01" value={fallback} onChange={e => setFallback(e.target.value)} /></Field>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        Price Lookup
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
        <Field label="Description patterns (one per line)">
          <textarea style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                    rows={4} value={patterns} onChange={e => setPatterns(e.target.value)}
                    placeholder="e.g., 22.5kw air cooled standby" />
        </Field>
        <Field label="SKU patterns (one per line)">
          <textarea style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                    rows={4} value={skuPats} onChange={e => setSkuPats(e.target.value)}
                    placeholder="e.g., RXSW100A" />
        </Field>
      </div>
      <Field label="Supplier preference (comma-separated, in order)">
        <input style={inputStyle} value={suppliers} onChange={e => setSuppliers(e.target.value)} placeholder="e.g., City Electric, EB Horsman" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
        <Field label="Min price (filter junk)"><input style={inputStyle} type="number" step="0.01" value={minPrice} onChange={e => setMinPrice(e.target.value)} placeholder="leave blank for none" /></Field>
        <Field label="Max price"><input style={inputStyle} type="number" step="0.01" value={maxPrice} onChange={e => setMaxPrice(e.target.value)} placeholder="leave blank for none" /></Field>
      </div>

      <div style={{ marginTop: 12 }}>
        <ConditionBuilder
          label="When this material is included"
          value={includeIf}
          schema={schema}
          onChange={setIncludeIf}
        />
      </div>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button style={btn("ghost")} onClick={onCancel} disabled={saving}>Cancel</button>
        <button style={btn("primary")} onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save node"}
        </button>
      </div>
    </div>
  );
}

// Lightweight summary line per node type so the user can read the tree quickly.
// Pass `schema` (from collectPlaybookSchema) for tier-label lookups so condition
// descriptions read "Tier is Gold 22kW" instead of "Tier is gold_22kw".
function summarizeNode(node, schema) {
  const c = node.config || {};
  const opts = { tierLabels: schema?.tierLabels || {} };
  const when = (cond) => {
    const d = describeCondition(cond, opts);
    return d === "Always" ? "" : ` · ${d}`;
  };
  switch (node.node_type) {
    case "tier_classifier": {
      const rules = (c.rules || []).length;
      const post  = (c.post  || []).length;
      return `${rules} tier rule(s)${post ? ` + ${post} post-adjustment(s)` : ""}`;
    }
    case "material": {
      const desc  = (c.description_template || c.sku_template || "").slice(0, 60);
      const fall  = c.fallback_price ?? "—";
      return `${desc} · $${fall}${when(c.include_if)}`;
    }
    case "material_picker": {
      const rules = (c.selection_rules || []).length;
      return `${rules} option(s) — picks one based on conditions`;
    }
    case "scope_item":
    case "note":
    case "exclusion": {
      const text = (c.text_template || c.text || "").slice(0, 110);
      return `"${text}"${when(c.include_if)}`;
    }
    case "labour_rule": {
      const base = c.base_hours;
      const adj  = (c.adjustments  || []).length;
      const rates = (c.rate_options || []).map(r => `$${r.rate}`).join(" / ");
      return `${base}h base · ${adj} adjustment(s) · rates ${rates}${when(c.include_if)}`;
    }
    case "hard_fail": {
      const code = c.code || "(no code)";
      const msg  = (c.message_template || c.message || "").slice(0, 80);
      const condText = c.condition === true || c.condition == null ? "Always" : describeCondition(c.condition, opts);
      return `${code} · "${msg}" · Fires when: ${condText}`;
    }
    default:
      return "";
  }
}

// Very basic interpolation (mirrors conditions.js#interpolate) so the activeIds
// matching can resolve {{genny_kw}} etc. Used only as a heuristic for the
// "● FIRED" badge.
function interpolateLite(tpl, vars) {
  if (typeof tpl !== "string") return tpl;
  return tpl.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_m, k) => {
    const v = vars?.[k];
    return v == null ? "" : String(v);
  });
}

// ── Preview inputs panel ───────────────────────────────────────────────────
function PreviewInputsCard({ inputs, onChange }) {
  const set = (patch) => onChange({ ...inputs, ...patch });
  return (
    <div style={{ ...cardStyle, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>
        Tweak these to see what different jobs would produce.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Panel amps">
          <select style={inputStyle} value={inputs.panel_amps}
                  onChange={e => set({ panel_amps: Number(e.target.value) })}>
            <option value={100}>100A</option>
            <option value={125}>125A</option>
            <option value={150}>150A</option>
            <option value={200}>200A</option>
          </select>
        </Field>
        <Field label="Load calc amps">
          <input style={inputStyle} type="number" value={inputs.load_calc_amps}
                 onChange={e => set({ load_calc_amps: Number(e.target.value) })} />
        </Field>
        <Field label="Cable feed length (m)">
          <input style={inputStyle} type="number" value={inputs.feed_length_m}
                 onChange={e => set({ feed_length_m: Number(e.target.value) })} />
        </Field>
        <Field label="Trenching">
          <select style={inputStyle} value={inputs.trenching}
                  onChange={e => set({ trenching: e.target.value })}>
            <option value="none">None</option>
            <option value="hand_under_20m">Hand &lt; 20m</option>
            <option value="hand_20_40m">Hand 20-40m</option>
            <option value="ditch_witch">Ditch witch</option>
            <option value="subcontractor">Subcontractor</option>
          </select>
        </Field>
      </div>
      <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 12 }}>
        <Check label="Hot tub on backup"          value={inputs.hot_tub_on_backup}        onChange={v => set({ hot_tub_on_backup: v })} />
        <Check label="Service upgrade combo"      value={inputs.service_upgrade_combo}    onChange={v => set({ service_upgrade_combo: v })} />
        <Check label="Panel replacement combo"    value={inputs.panel_replacement_combo}  onChange={v => set({ panel_replacement_combo: v })} />
        <Check label="Client owns generator"      value={inputs.client_owns_generator}    onChange={v => set({ client_owns_generator: v })} />
      </div>
    </div>
  );
}

function PreviewResultCard({ preview, loading, error }) {
  if (loading) return <div style={{ ...cardStyle, color: C.muted, textAlign: "center" }}>Running preview…</div>;
  if (error)   return <div style={{ ...cardStyle, color: C.danger }}>{error}</div>;
  if (!preview) return null;
  const m = preview.meta || {};
  const t = preview.totals || {};

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Pill color={C.accent}>{m.tier_label || m.tier}</Pill>
        {m.genny_kw && <Pill color="#7dd3fc">{m.genny_kw}kW</Pill>}
        {m.ats_choice && <Pill color="#fbbf24">{m.ats_choice}</Pill>}
        {m.panel_workaround && <Pill color={C.warn}>{m.panel_workaround}</Pill>}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={subheadStyle}>Materials ({(preview.materials || []).length})</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {(preview.materials || []).map(m => (
            <div key={m.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, gap: 8 }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ color: C.muted }}>{m.qty} {m.unit} </span>
                <span>{m.description}</span>
                {m._price_source && (
                  <span style={{ marginLeft: 6, fontSize: 10, color: priceSourceColor(m._price_source) }}>
                    {priceSourceDot(m._price_source)}
                  </span>
                )}
              </span>
              <span style={{ color: C.text, fontWeight: 600 }}>{fmt$(m.client_total)}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={subheadStyle}>Scope ({(preview.scope || []).length})</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.text }}>
          {(preview.scope || []).map(s => <li key={s.id}>{s.text}</li>)}
        </ul>
      </div>

      {(preview.notes || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={subheadStyle}>Notes</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.muted }}>
            {preview.notes.map(n => <li key={n.id}>{n.text}</li>)}
          </ul>
        </div>
      )}

      {(preview.exclusions || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={subheadStyle}>Exclusions</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.muted }}>
            {preview.exclusions.map(e => <li key={e.id}>{e.text}</li>)}
          </ul>
        </div>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, fontSize: 12 }}>
        <Row label="Materials (PST in)">{fmt$(t.materials_subtotal)}</Row>
        <Row label={`Labour (${preview.labour?.total_hours}h × $${preview.labour?.rate})`}>{fmt$(t.labour_total)}</Row>
        <Row label="Permit">{fmt$(t.permit_fee)}</Row>
        <Row label="SUBTOTAL" strong>{fmt$(t.subtotal)}</Row>
        <Row label="+ GST 5% (added in Jobber)">{fmt$((t.subtotal || 0) * 0.05)}</Row>
      </div>

      {(preview.review_flags || []).length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={subheadStyle}>Review flags</div>
          {preview.review_flags.map((f, i) => (
            <div key={i} style={{ fontSize: 11, color: f.severity === "error" ? C.danger : C.warn, marginBottom: 2 }}>
              ⚠ {f.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Text-only editor: scope_item / note / exclusion ────────────────────────
function TextNodeEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  const [display, setDisplay] = useState(node.display_label || "");
  const [text,    setText]    = useState(c.text_template || c.text || "");
  const [includeIf, setIncludeIf] = useState(c.include_if ?? null);

  function handleSave() {
    onSave({
      display_label: display || null,
      config: {
        ...c,
        text_template: text,
        include_if: includeIf || undefined,
      },
    });
  }

  return (
    <div style={editorBoxStyle}>
      <Field label="Display label (optional, internal)">
        <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} placeholder="Short internal label" />
      </Field>
      <Field label="Text shown to client / on quote">
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 60 }}
                  value={text} onChange={e => setText(e.target.value)}
                  placeholder="e.g., Run #1 ACWU power cable from generator to ATS" />
      </Field>
      <ConditionBuilder
        label="When this fires"
        value={includeIf}
        schema={schema}
        onChange={setIncludeIf}
      />
      <EditorFooter onCancel={onCancel} onSave={handleSave} saving={saving} />
    </div>
  );
}

// ── Labour Rule editor ──────────────────────────────────────────────────────
function LabourRuleEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  const [display, setDisplay] = useState(node.display_label || "");
  const [baseHours, setBaseHours] = useState(String(c.base_hours ?? 16));
  // Each adjustment keeps its `if` as a structured condition (or null = always)
  const [adjustments, setAdjustments] = useState(
    (c.adjustments || []).map(a => ({
      name: a.name || "",
      hours: String(a.hours ?? 0),
      ifExpr: a.if ?? null,
    }))
  );
  const [includeIf, setIncludeIf] = useState(c.include_if ?? null);
  const [rateOptions, setRateOptions] = useState(
    (c.rate_options || [{ name: "standard", rate: 280, default: true }]).map(r => ({
      name: r.name || "",
      rate: String(r.rate ?? 0),
      default: !!r.default,
      competitive: !!r.competitive,
    }))
  );
  const [error, setError] = useState(null);

  function setAdj(i, patch) { setAdjustments(a => a.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  function addAdj() { setAdjustments(a => [...a, { name: "", hours: "0", ifExpr: null }]); }
  function rmAdj(i) { setAdjustments(a => a.filter((_, idx) => idx !== i)); }
  function setRate(i, patch) { setRateOptions(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  function addRate() { setRateOptions(r => [...r, { name: "", rate: "0", default: false, competitive: false }]); }
  function rmRate(i) { setRateOptions(r => r.filter((_, idx) => idx !== i)); }

  function handleSave() {
    setError(null);
    const adjs = [];
    for (let i = 0; i < adjustments.length; i++) {
      const a = adjustments[i];
      if (!a.name && !a.ifExpr) continue;
      adjs.push({ name: a.name, hours: Number(a.hours) || 0, ...(a.ifExpr ? { if: a.ifExpr } : {}) });
    }
    const rates = rateOptions.map(r => ({
      name: r.name, rate: Number(r.rate) || 0,
      ...(r.default     ? { default: true }     : {}),
      ...(r.competitive ? { competitive: true } : {}),
    }));
    onSave({
      display_label: display || null,
      config: {
        ...c,
        base_hours: Number(baseHours) || 0,
        adjustments: adjs,
        rate_options: rates,
        include_if: includeIf || undefined,
      },
    });
  }

  return (
    <div style={editorBoxStyle}>
      <Field label="Display label">
        <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} />
      </Field>
      <Field label="Base hours (e.g. 16 = 2 van-days @ 8h)">
        <input style={inputStyle} type="number" step="0.5" value={baseHours} onChange={e => setBaseHours(e.target.value)} />
      </Field>

      <ConditionBuilder
        label="When this labour rule applies"
        value={includeIf}
        schema={schema}
        onChange={setIncludeIf}
      />

      <div style={subhead}>Add hours when these things happen</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {adjustments.map((a, i) => (
          <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 30px", gap: 6, marginBottom: 8, alignItems: "center" }}>
              <input style={inputStyle} placeholder="What is this for? (e.g. Trenching > 30m)" value={a.name} onChange={e => setAdj(i, { name: e.target.value })} />
              <input style={inputStyle} type="number" step="0.5" placeholder="+ hrs" value={a.hours} onChange={e => setAdj(i, { hours: e.target.value })} />
              <button onClick={() => rmAdj(i)} style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }} title="Remove this adjustment">×</button>
            </div>
            <ConditionBuilder
              label="When to add these hours"
              value={a.ifExpr}
              schema={schema}
              onChange={(expr) => setAdj(i, { ifExpr: expr })}
            />
          </div>
        ))}
        <button onClick={addAdj} style={{ ...btn("ghost"), fontSize: 11, alignSelf: "flex-start" }}>+ Add adjustment</button>
      </div>

      <div style={subhead}>Rate options</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rateOptions.map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 100px 80px 100px 30px", gap: 6 }}>
            <input style={inputStyle} placeholder="standard / competitive" value={r.name} onChange={e => setRate(i, { name: e.target.value })} />
            <input style={inputStyle} type="number" placeholder="rate $" value={r.rate} onChange={e => setRate(i, { rate: e.target.value })} />
            <label style={checkLbl}><input type="checkbox" checked={r.default} onChange={e => setRate(i, { default: e.target.checked })} /> default</label>
            <label style={checkLbl}><input type="checkbox" checked={r.competitive} onChange={e => setRate(i, { competitive: e.target.checked })} /> competitive</label>
            <button onClick={() => rmRate(i)} style={{ ...btn("ghost"), padding: "4px 6px", fontSize: 11, color: C.danger }}>×</button>
          </div>
        ))}
        <button onClick={addRate} style={{ ...btn("ghost"), fontSize: 11, alignSelf: "flex-start" }}>+ Add rate</button>
      </div>

      {error && <div style={{ fontSize: 11, color: C.danger, marginTop: 6 }}>{error}</div>}
      <EditorFooter onCancel={onCancel} onSave={handleSave} saving={saving} />
    </div>
  );
}

// ── Hard-fail editor ────────────────────────────────────────────────────────
function HardFailEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  const [display, setDisplay] = useState(node.display_label || "");
  const [code,    setCode]    = useState(c.code || "");
  const [severity, setSeverity] = useState(c.severity || "warn");
  const [message, setMessage] = useState(c.message_template || c.message || "");
  // Treat `condition: true` as "always fire". null/undefined also means
  // "no condition gating" (the executor defaults to firing). For UI we
  // normalize both to null so the ConditionBuilder shows "Always".
  const [condition, setCondition] = useState(c.condition === true ? null : (c.condition ?? null));

  function handleSave() {
    onSave({
      display_label: display || null,
      config: { ...c, code, severity, message_template: message,
        ...(condition !== undefined ? { condition } : { condition: undefined }),
      },
    });
  }

  return (
    <div style={editorBoxStyle}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 140px", gap: 8 }}>
        <Field label="Display label (internal)">
          <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} />
        </Field>
        <Field label="Code (machine ID)">
          <input style={inputStyle} value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. bc_hydro_unscheduled" />
        </Field>
        <Field label="Severity">
          <select style={inputStyle} value={severity} onChange={e => setSeverity(e.target.value)}>
            <option value="info">info</option>
            <option value="warn">warn</option>
            <option value="error">error</option>
          </select>
        </Field>
      </div>
      <Field label="Message (shown to tech when this fires)">
        <textarea style={{ ...inputStyle, resize: "vertical", minHeight: 60 }} value={message} onChange={e => setMessage(e.target.value)} />
      </Field>
      <ConditionBuilder
        label="Fire this check when"
        value={condition}
        schema={schema}
        onChange={setCondition}
      />
      <EditorFooter onCancel={onCancel} onSave={handleSave} saving={saving} />
    </div>
  );
}

// ── Tier Classifier editor ──────────────────────────────────────────────────
// Visual editor for the tier_classifier node. Each playbook has exactly one
// of these and it's the "brain" — runs first and picks the tier (Bronze /
// Silver / Gold / ...) plus any tier-derived flags (genny_kw, ats_choice, etc.).
//
// Shape it edits:
//   config: {
//     rules: [
//       { if: <condition>, set: { tier, tier_label, ...other fields } },
//       ...
//       { default: true,   set: { tier, tier_label, ... } }   // fallback (always last)
//     ],
//     post: [ { if: <condition>, set: {...} } ]   // optional post-rules
//   }
// We render each rule as a card with the ConditionBuilder for `if` and a
// SetEditor for `set`. The default rule is always shown last and can't be
// removed (it's the safety net when no rule matches).
function TierClassifierEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  const [display, setDisplay] = useState(node.display_label || "");

  // Split rules into ordered list + the default. We rebuild on save.
  const initialRules = (c.rules || []).filter(r => !r.default).map(r => ({
    ifExpr: r.if ?? null,
    set:    r.set || {},
  }));
  const initialDefault = (c.rules || []).find(r => r.default)?.set || { tier: "default", tier_label: "Default" };
  const initialPost  = (c.post || []).map(r => ({ ifExpr: r.if ?? null, set: r.set || {} }));

  const [rules,    setRules]    = useState(initialRules);
  const [defaultSet, setDefaultSet] = useState(initialDefault);
  const [post,     setPost]     = useState(initialPost);

  function updateRule(i, patch) { setRules(r => r.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  function addRule()             { setRules(r => [...r, { ifExpr: null, set: { tier: "", tier_label: "" } }]); }
  function rmRule(i)             { setRules(r => r.filter((_, idx) => idx !== i)); }
  function moveRule(i, dir)      {
    setRules(r => {
      const next = r.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return r;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }
  function updatePost(i, patch) { setPost(p => p.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  function addPost()            { setPost(p => [...p, { ifExpr: null, set: {} }]); }
  function rmPost(i)            { setPost(p => p.filter((_, idx) => idx !== i)); }

  function handleSave() {
    const rulesOut = rules.map(r => ({
      ...(r.ifExpr ? { if: r.ifExpr } : {}),
      set: r.set,
    }));
    rulesOut.push({ default: true, set: defaultSet });
    const postOut = post.map(r => ({
      ...(r.ifExpr ? { if: r.ifExpr } : {}),
      set: r.set,
    }));
    onSave({
      display_label: display || null,
      config: { ...c, rules: rulesOut, ...(postOut.length ? { post: postOut } : { post: undefined }) },
    });
  }

  return (
    <div style={editorBoxStyle}>
      <Field label="Display label">
        <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} />
      </Field>

      <div style={subhead}>Tier rules (first match wins)</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {rules.map((r, i) => (
          <TierRuleCard
            key={i}
            index={i}
            total={rules.length}
            rule={r}
            schema={schema}
            onChange={(patch) => updateRule(i, patch)}
            onMoveUp={() => moveRule(i, -1)}
            onMoveDown={() => moveRule(i, 1)}
            onRemove={() => rmRule(i)}
          />
        ))}
        <button onClick={addRule} style={{ ...btn("ghost"), fontSize: 12, alignSelf: "flex-start" }}>+ Add tier rule</button>
      </div>

      <div style={{ ...subhead, marginTop: 12 }}>Default tier (used when no rule above matches)</div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
        <SetEditor value={defaultSet} onChange={setDefaultSet} schema={schema} kind="tier" />
      </div>

      {post.length > 0 || (
        <button onClick={addPost} style={{ ...btn("ghost"), fontSize: 11, marginTop: 8, alignSelf: "flex-start" }}>+ Add post-rule (advanced — runs after initial tier is picked)</button>
      )}
      {post.length > 0 && (
        <>
          <div style={{ ...subhead, marginTop: 12 }}>Post-rules (override fields after initial tier is picked)</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {post.map((r, i) => (
              <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Post-rule {i + 1}</div>
                  <button onClick={() => rmPost(i)} style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }}>×</button>
                </div>
                <ConditionBuilder
                  label="When to fire this override"
                  value={r.ifExpr}
                  schema={schema}
                  onChange={(expr) => updatePost(i, { ifExpr: expr })}
                />
                <div style={{ ...subhead, marginTop: 8 }}>Override these fields:</div>
                <SetEditor value={r.set} onChange={(s) => updatePost(i, { set: s })} schema={schema} kind="post" />
              </div>
            ))}
            <button onClick={addPost} style={{ ...btn("ghost"), fontSize: 11, alignSelf: "flex-start" }}>+ Add post-rule</button>
          </div>
        </>
      )}

      <EditorFooter onCancel={onCancel} onSave={handleSave} saving={saving} />
    </div>
  );
}

// One tier rule card: ConditionBuilder for `if` + SetEditor for `set` + reorder/remove
function TierRuleCard({ index, total, rule, schema, onChange, onMoveUp, onMoveDown, onRemove }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Rule {index + 1} of {total}</div>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={onMoveUp}   disabled={index === 0}            style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, opacity: index === 0 ? 0.4 : 1 }} title="Move up">↑</button>
          <button onClick={onMoveDown} disabled={index === total - 1}    style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, opacity: index === total - 1 ? 0.4 : 1 }} title="Move down">↓</button>
          <button onClick={onRemove}                                      style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }} title="Remove this rule">×</button>
        </div>
      </div>
      <ConditionBuilder
        label="When this rule matches"
        value={rule.ifExpr}
        schema={schema}
        onChange={(expr) => onChange({ ifExpr: expr })}
      />
      <div style={{ ...subhead, marginTop: 8 }}>Set these fields:</div>
      <SetEditor
        value={rule.set || {}}
        onChange={(s) => onChange({ set: s })}
        schema={schema}
        kind="tier"
      />
    </div>
  );
}

// SetEditor — key/value editor for the `set` object on tier_classifier and
// material_picker rules. Renders well-known fields (tier, tier_label) as
// dedicated rows at the top, then arbitrary extra fields below.
function SetEditor({ value, onChange, schema, kind = "tier" }) {
  const known = kind === "tier"
    ? [
        { key: "tier",        label: "Tier slug",  type: "string", placeholder: "e.g. silver_partial" },
        { key: "tier_label",  label: "Tier label (shown to tech)", type: "string", placeholder: "e.g. Silver — Partial backup" },
      ]
    : [];
  const knownKeys  = new Set(known.map(k => k.key));
  const extraKeys  = Object.keys(value || {}).filter(k => !knownKeys.has(k));
  // Suggest fields from the playbook's schema for the "add field" picker
  const suggestableFields = (schema?.fields || [])
    .map(f => f.name)
    .filter(name => !knownKeys.has(name) && !extraKeys.includes(name));

  function setKey(k, v) {
    // Smart-parse: if it looks like a number, store as number; "true"/"false" → boolean
    let parsed = v;
    if (typeof v === "string") {
      if (v === "true")  parsed = true;
      else if (v === "false") parsed = false;
      else if (v !== "" && !isNaN(Number(v)) && /^-?\d+(\.\d+)?$/.test(v.trim())) parsed = Number(v);
    }
    onChange({ ...value, [k]: parsed });
  }
  function removeKey(k) {
    const next = { ...value };
    delete next[k];
    onChange(next);
  }
  function addField(k) {
    if (!k) return;
    onChange({ ...value, [k]: "" });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {known.map(k => (
        <div key={k.key} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: 6, alignItems: "center" }}>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{k.label}</div>
          <input
            style={{ ...inputStyle, fontSize: 12 }}
            value={value?.[k.key] != null ? String(value[k.key]) : ""}
            placeholder={k.placeholder}
            onChange={(e) => setKey(k.key, e.target.value)}
          />
        </div>
      ))}
      {extraKeys.map(k => {
        const v = value[k];
        const isBool = typeof v === "boolean";
        return (
          <div key={k} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr) 30px", gap: 6, alignItems: "center" }}>
            <div style={{ fontSize: 11, color: C.text, fontWeight: 600 }}>{friendlyField(k)}</div>
            {isBool ? (
              <select style={{ ...inputStyle, fontSize: 12 }} value={String(v)} onChange={(e) => setKey(k, e.target.value)}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            ) : (
              <input
                style={{ ...inputStyle, fontSize: 12 }}
                value={v == null ? "" : String(v)}
                onChange={(e) => setKey(k, e.target.value)}
                placeholder="value"
              />
            )}
            <button onClick={() => removeKey(k)} style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }} title="Remove field">×</button>
          </div>
        );
      })}
      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
        <select
          style={{ ...inputStyle, fontSize: 11, maxWidth: 220 }}
          value=""
          onChange={(e) => { if (e.target.value) addField(e.target.value); e.target.value = ""; }}
        >
          <option value="">+ Add field…</option>
          {suggestableFields.map(name => (
            <option key={name} value={name}>{friendlyField(name)} ({name})</option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: C.muted }}>or</span>
        <input
          style={{ ...inputStyle, fontSize: 11, maxWidth: 200 }}
          placeholder="custom_field_name"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              addField(e.target.value.trim());
              e.target.value = "";
            }
          }}
        />
      </div>
    </div>
  );
}

// ── Material Picker editor ──────────────────────────────────────────────────
// Visual editor for a material_picker. Shape:
//   config: {
//     include_if: <optional outer condition>,
//     selection_rules: [
//       { if: <condition>, set: <material fields> },
//       ...
//       { default: true,   set: <material fields> }
//     ]
//   }
// Each "set" is a material spec (sku_template, description_template, qty, unit,
// markup_pct, pst_pct, fallback_price, lookup{...}). We reuse the same shape
// from MaterialNodeEditor — just embedded inside a rule card.
function MaterialPickerEditor({ node, onCancel, onSave, saving, schema }) {
  const c = node.config || {};
  const [display, setDisplay] = useState(node.display_label || "");
  const [includeIf, setIncludeIf] = useState(c.include_if ?? null);

  const initial = (c.selection_rules || []).filter(r => !r.default).map(r => ({
    ifExpr: r.if ?? null,
    set:    r.set || {},
  }));
  const initialDefault = (c.selection_rules || []).find(r => r.default)?.set || {
    sku_template: "", description_template: "", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0,
  };
  const [options,       setOptions]       = useState(initial);
  const [defaultOption, setDefaultOption] = useState(initialDefault);

  function updateOption(i, patch) { setOptions(o => o.map((x, idx) => idx === i ? { ...x, ...patch } : x)); }
  function addOption()             { setOptions(o => [...o, { ifExpr: null, set: { sku_template: "", description_template: "", qty: 1, unit: "ea", markup_pct: 35, pst_pct: 7, fallback_price: 0 } }]); }
  function rmOption(i)             { setOptions(o => o.filter((_, idx) => idx !== i)); }
  function moveOption(i, dir)      {
    setOptions(o => {
      const next = o.slice();
      const j = i + dir;
      if (j < 0 || j >= next.length) return o;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  function handleSave() {
    const rules = options.map(o => ({
      ...(o.ifExpr ? { if: o.ifExpr } : {}),
      set: o.set,
    }));
    rules.push({ default: true, set: defaultOption });
    onSave({
      display_label: display || null,
      config: { ...c, include_if: includeIf || undefined, selection_rules: rules },
    });
  }

  return (
    <div style={editorBoxStyle}>
      <Field label="Display label">
        <input style={inputStyle} value={display} onChange={e => setDisplay(e.target.value)} />
      </Field>
      <ConditionBuilder
        label="When this picker fires at all"
        value={includeIf}
        schema={schema}
        onChange={setIncludeIf}
      />

      <div style={{ ...subhead, marginTop: 12 }}>Options (picker uses the first one that matches)</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {options.map((o, i) => (
          <MaterialPickerOptionCard
            key={i}
            index={i}
            total={options.length}
            option={o}
            schema={schema}
            onChange={(patch) => updateOption(i, patch)}
            onMoveUp={() => moveOption(i, -1)}
            onMoveDown={() => moveOption(i, 1)}
            onRemove={() => rmOption(i)}
          />
        ))}
        <button onClick={addOption} style={{ ...btn("ghost"), fontSize: 12, alignSelf: "flex-start" }}>+ Add option</button>
      </div>

      <div style={{ ...subhead, marginTop: 12 }}>Default option (used when no option above matches)</div>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
        <MaterialSpecEditor value={defaultOption} onChange={setDefaultOption} />
      </div>

      <EditorFooter onCancel={onCancel} onSave={handleSave} saving={saving} />
    </div>
  );
}

function MaterialPickerOptionCard({ index, total, option, schema, onChange, onMoveUp, onMoveDown, onRemove }) {
  const desc = option.set?.description_template || option.set?.sku_template || `Option ${index + 1}`;
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, gap: 8 }}>
        <div style={{ fontSize: 12, color: C.text, fontWeight: 600, minWidth: 0, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {desc}
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button onClick={onMoveUp}   disabled={index === 0}         style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, opacity: index === 0 ? 0.4 : 1 }} title="Move up">↑</button>
          <button onClick={onMoveDown} disabled={index === total - 1} style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, opacity: index === total - 1 ? 0.4 : 1 }} title="Move down">↓</button>
          <button onClick={onRemove}                                  style={{ ...btn("ghost"), padding: "4px 8px", fontSize: 12, color: C.danger }} title="Remove option">×</button>
        </div>
      </div>
      <ConditionBuilder
        label="When to use this option"
        value={option.ifExpr}
        schema={schema}
        onChange={(expr) => onChange({ ifExpr: expr })}
      />
      <div style={{ ...subhead, marginTop: 8 }}>Material details:</div>
      <MaterialSpecEditor
        value={option.set || {}}
        onChange={(set) => onChange({ set })}
      />
    </div>
  );
}

// Inline material spec form — same fields as MaterialNodeEditor but lives inside
// a picker option (so no display_label / include_if at this level).
function MaterialSpecEditor({ value, onChange }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const lookup = v.lookup || {};
  const setLookup = (patch) => set({ lookup: { ...lookup, ...patch } });

  // qty can be either a number or { field, default } resolver
  const qtyIsResolver = typeof v.qty === "object" && v.qty !== null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <Field label="SKU"><input style={{ ...inputStyle, fontSize: 12 }} value={v.sku_template || ""} onChange={(e) => set({ sku_template: e.target.value })} /></Field>
        <Field label="Description"><input style={{ ...inputStyle, fontSize: 12 }} value={v.description_template || ""} onChange={(e) => set({ description_template: e.target.value })} /></Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 6 }}>
        <Field label="Qty">
          <input style={{ ...inputStyle, fontSize: 12 }}
                 value={qtyIsResolver ? "" : String(v.qty ?? "1")}
                 onChange={(e) => set({ qty: e.target.value === "" ? 1 : Number(e.target.value) })}
                 disabled={qtyIsResolver} />
          {qtyIsResolver && <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>(from {v.qty.field})</div>}
        </Field>
        <Field label="Unit"><input style={{ ...inputStyle, fontSize: 12 }} value={v.unit || "ea"} onChange={(e) => set({ unit: e.target.value })} /></Field>
        <Field label="Markup %"><input style={{ ...inputStyle, fontSize: 12 }} type="number" value={v.markup_pct ?? 35} onChange={(e) => set({ markup_pct: Number(e.target.value) || 0 })} /></Field>
        <Field label="PST %"><input style={{ ...inputStyle, fontSize: 12 }} type="number" value={v.pst_pct ?? 7} onChange={(e) => set({ pst_pct: Number(e.target.value) || 0 })} /></Field>
        <Field label="Fallback $"><input style={{ ...inputStyle, fontSize: 12 }} type="number" step="0.01" value={v.fallback_price ?? 0} onChange={(e) => set({ fallback_price: Number(e.target.value) || 0 })} /></Field>
      </div>
      <div style={{ ...subhead, marginTop: 4 }}>Price lookup</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <Field label="Description patterns (one per line)">
          <textarea style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                    rows={3}
                    value={(lookup.patterns_template || lookup.patterns || []).join("\n")}
                    onChange={(e) => setLookup({ patterns_template: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
        </Field>
        <Field label="SKU patterns (one per line)">
          <textarea style={{ ...inputStyle, resize: "vertical", fontFamily: "ui-monospace, monospace", fontSize: 11 }}
                    rows={3}
                    value={(lookup.sku_patterns_template || lookup.sku_patterns || []).join("\n")}
                    onChange={(e) => setLookup({ sku_patterns_template: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })} />
        </Field>
      </div>
      <Field label="Supplier preference (comma-separated)">
        <input style={{ ...inputStyle, fontSize: 12 }} value={(lookup.supplier_preference || []).join(", ")} onChange={(e) => setLookup({ supplier_preference: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <Field label="Min price (filter junk)"><input style={{ ...inputStyle, fontSize: 12 }} type="number" step="0.01" value={lookup.min_price ?? ""} onChange={(e) => setLookup({ min_price: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="(none)" /></Field>
        <Field label="Max price"><input style={{ ...inputStyle, fontSize: 12 }} type="number" step="0.01" value={lookup.max_price ?? ""} onChange={(e) => setLookup({ max_price: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="(none)" /></Field>
      </div>
    </div>
  );
}

// Shared bits for the new editors
const editorBoxStyle = { marginTop: 10, padding: 10, background: C.surface2, borderRadius: 8, border: `1px solid ${C.border}`, display: "flex", flexDirection: "column", gap: 8 };
const subhead = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginTop: 6, marginBottom: 2 };
const checkLbl = { fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" };
function EditorFooter({ onCancel, onSave, saving }) {
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
      <button style={btn("ghost")} onClick={onCancel} disabled={saving}>Cancel</button>
      <button style={btn("primary")} onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save node"}</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Suggestions Inbox — patterns the learning engine detected
//
// Sits at the top of the template detail page. Each card describes a
// proposed rule, the evidence behind it, and (for Phase B5) Dismiss/Snooze
// actions. Phase B6 will add Approve — for now this is read-only so the
// detection logic can prove out before we wire it into playbook mutation.
// ─────────────────────────────────────────────────────────────────────────────
function SuggestionsInbox({ suggestions, loading, analyzing, analyzeMessage, onAnalyze, onAction, onApprove }) {
  if (loading && (!suggestions || suggestions.length === 0)) {
    return null; // quiet while loading
  }

  const hasSuggestions = suggestions && suggestions.length > 0;

  return (
    <div style={{
      marginBottom: 16,
      background: C.surface,
      border: `1px solid ${hasSuggestions ? C.accent : C.border}`,
      borderRadius: 12,
      padding: 14,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: hasSuggestions ? 10 : 0, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>
            💡 Suggestions inbox
          </div>
          {hasSuggestions && (
            <span style={{
              background: C.accentDim,
              color: C.accent,
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
            }}>
              {suggestions.length} pending
            </span>
          )}
        </div>
        <button
          onClick={onAnalyze}
          disabled={analyzing}
          style={{ ...btn("ghost"), padding: "6px 12px", fontSize: 12 }}
          title="Re-scan all Save & Learn'd quotes for new patterns"
        >
          {analyzing ? "Scanning…" : "🔄 Scan for new patterns"}
        </button>
      </div>

      {analyzeMessage && (
        <div style={{
          fontSize: 12,
          color: analyzeMessage.startsWith("Error") ? C.danger : C.muted,
          marginBottom: hasSuggestions ? 10 : 0,
          padding: "6px 10px",
          background: C.surface2,
          borderRadius: 6,
        }}>
          {analyzeMessage}
        </div>
      )}

      {!hasSuggestions && !analyzeMessage && (
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginTop: 4 }}>
          No suggestions yet. Click <strong>Scan for new patterns</strong> to mine both:{" "}
          (1) drafts you marked with{" "}
          <code style={{ background: C.surface2, padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>💡 Save &amp; Learn</code>{" "}
          for cross-quote patterns, and (2) historical won jobs from Jobber for items the playbook doesn't cover yet.
        </div>
      )}

      {hasSuggestions && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {suggestions.map(s => (
            <SuggestionCard key={s.id} suggestion={s} onAction={onAction} onApprove={onApprove} />
          ))}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ suggestion, onAction, onApprove }) {
  const typeIcon = {
    add_material:  "📦",
    add_scope:     "📝",
    add_note:      "📌",
    adjust_labour: "💼",
  }[suggestion.suggestion_type] || "💡";
  const typeLabel = {
    add_material:  "Add material",
    add_scope:     "Add scope step",
    add_note:      "Add note",
    adjust_labour: "Adjust labour",
  }[suggestion.suggestion_type] || suggestion.suggestion_type;

  const trigger = suggestion.trigger_pattern;
  const triggerText = trigger
    ? describeCondition(trigger)
    : "Any quote in this template";

  return (
    <div style={{
      background: C.surface2,
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${C.accent}`,
      borderRadius: 8,
      padding: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700, marginBottom: 4, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <span>{typeIcon} {typeLabel}</span>
            <SuggestionSourceBadge source={suggestion.source} />
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6 }}>
            {suggestion.title}
          </div>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.45 }}>
            <span style={{ color: C.text, fontWeight: 600 }}>When:</span> {triggerText}
          </div>
          {suggestion.rationale && (
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.45, marginTop: 3 }}>
              {suggestion.rationale}
            </div>
          )}
        </div>
        <div style={{
          background: C.accentDim,
          color: C.accent,
          padding: "3px 8px",
          borderRadius: 999,
          fontSize: 10,
          fontWeight: 700,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          ✓ {suggestion.evidence_count}×
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
        <button
          onClick={() => onApprove && onApprove(suggestion.id)}
          style={{ ...btn("primary"), fontSize: 11, padding: "4px 12px" }}
          title="Add this as a rule on the playbook. The new node lands in the tree below — you can fine-tune it from there."
        >
          ✓ Approve &amp; add
        </button>
        <button
          onClick={() => onAction(suggestion.id, "dismissed")}
          style={{ ...btn("ghost"), fontSize: 11, padding: "4px 10px" }}
          title="Not a pattern worth keeping — hide forever"
        >
          Dismiss
        </button>
        <button
          onClick={() => onAction(suggestion.id, "snoozed", 30)}
          style={{ ...btn("ghost"), fontSize: 11, padding: "4px 10px" }}
          title="Hide for 30 days — re-evaluate later if pattern persists"
        >
          Snooze 30d
        </button>
      </div>
    </div>
  );
}

// Small badge that tells the user where the suggestion came from. Marked
// quotes = "tech did this 3+ times in recent drafts". Historical jobs =
// "we've billed this in 3+ won jobs but the playbook doesn't know about it."
function SuggestionSourceBadge({ source }) {
  const meta = source === "historical_jobs"
    ? { label: "from win history", color: "#7dd3fc", bg: "rgba(125, 211, 252, 0.12)" }
    : { label: "from your edits",  color: "#a5ce3a", bg: "rgba(165, 206, 58, 0.12)" };
  return (
    <span style={{
      background: meta.bg,
      color:      meta.color,
      padding:    "1px 8px",
      borderRadius: 999,
      fontSize:   9,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    }}>
      {meta.label}
    </span>
  );
}

// ── Historical Quotes panel: real Browning history for this service family ─
// Pulls from jb_quotes (with title keyword fallback) and matches to jb_jobs
// by quote_num. Each quote row is expandable to show the actual line items
// Browning sent to the client OR the job-side breakdown for wons.
function HistoricalQuotes({ history, loading, error }) {
  const [expandedId, setExpandedId] = useState(null);
  const [verdictFilter, setVerdictFilter] = useState("all"); // 'all' | 'WON' | 'LOST' | 'OPEN'

  if (loading) {
    return (
      <div style={{ marginTop: 16 }}>
        <SectionTitle>📜 Historical Quotes</SectionTitle>
        <div style={{ ...cardStyle, color: C.muted, textAlign: "center" }}>Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ marginTop: 16 }}>
        <SectionTitle>📜 Historical Quotes</SectionTitle>
        <div style={{ ...cardStyle, color: C.danger }}>Error: {error}</div>
      </div>
    );
  }
  if (!history || !history.quotes?.length) return null;

  const s = history.summary || {};
  const filtered = history.quotes.filter(q =>
    verdictFilter === "all" ? true : q.verdict === verdictFilter
  );

  return (
    <div style={{ marginTop: 16 }}>
      <SectionTitle>📜 Historical Quotes ({s.matched} matched · {s.won} won)</SectionTitle>
      <div style={cardStyle}>
        {/* Summary row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
          <Stat label="Win rate"        value={`${s.win_rate_pct}%`}     hint={`${s.won}/${s.won + s.lost} closed`} />
          <Stat label="Avg quoted"      value={fmt$(s.avg_quoted)}        />
          <Stat label="Avg won revenue" value={fmt$(s.avg_won_revenue)}   hint="on jobs with revenue data" />
          <Stat label="Avg profit ($)"  value={fmt$(s.avg_won_profit)}    />
        </div>

        {/* Filter chips */}
        <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
          {[["all",`All (${s.matched})`],["WON",`Won (${s.won})`],["LOST",`Lost (${s.lost})`],["OPEN",`Open (${s.open})`]].map(([k, l]) => (
            <button key={k} onClick={() => setVerdictFilter(k)}
                    style={{
                      ...btn(verdictFilter === k ? "primary" : "ghost"),
                      padding: "3px 10px", fontSize: 11,
                    }}>
              {l}
            </button>
          ))}
        </div>

        {/* Quote list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 600, overflowY: "auto" }}>
          {filtered.map(q => {
            const isExpanded = expandedId === q.quote_num;
            return (
              <div key={q.quote_num} style={{
                background: isExpanded ? C.surface2 : "transparent",
                borderRadius: 6,
                borderTop: `1px solid ${C.border}`,
              }}>
                <button
                  onClick={() => setExpandedId(isExpanded ? null : q.quote_num)}
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 10px", display: "grid",
                    gridTemplateColumns: "60px 60px 1fr 90px 90px 20px",
                    gap: 8, alignItems: "center", background: "none", border: "none", cursor: "pointer",
                    color: C.text, fontFamily: "inherit", fontSize: 12,
                  }}
                >
                  <VerdictBadge v={q.verdict} />
                  <span style={{ color: C.muted, fontSize: 11 }}>Q#{q.quote_num}</span>
                  <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {q.client} <span style={{ color: C.muted }}>· {(q.title || "").slice(0, 50)}</span>
                  </span>
                  <span style={{ textAlign: "right" }}>{fmt$(q.total)}</span>
                  <span style={{ textAlign: "right", color: q.job ? C.good : C.muted, fontSize: 11 }}>
                    {q.job?.profit_pct ? `${q.job.profit_pct.toFixed(1)}%` : "—"}
                  </span>
                  <span style={{ color: C.muted }}>{isExpanded ? "▼" : "▸"}</span>
                </button>

                {isExpanded && (
                  <div style={{ padding: "0 10px 12px 10px", fontSize: 11, color: C.muted }}>
                    {/* Dates row */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 8 }}>
                      {q.drafted_date  && <span><strong style={{color:C.text}}>Drafted:</strong> {q.drafted_date}</span>}
                      {q.sent_date     && <span><strong style={{color:C.text}}>Sent:</strong> {q.sent_date}</span>}
                      {q.converted_date && q.converted_date !== "-" && <span><strong style={{color:C.good}}>Converted:</strong> {q.converted_date}</span>}
                      {q.service_type  && <span><strong style={{color:C.text}}>Type:</strong> {q.service_type}</span>}
                    </div>

                    {q.job && (
                      <div style={{ background: "rgba(16,185,129,0.06)", border: `1px solid rgba(16,185,129,0.25)`, padding: 8, borderRadius: 6, marginBottom: 8 }}>
                        <div style={{ fontSize: 11, color: C.good, fontWeight: 700, marginBottom: 4 }}>
                          ✅ Won — Job #{q.job.job_num}{q.job.closed_date ? ` (closed ${q.job.closed_date})` : ""}
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, fontSize: 11, color: C.text }}>
                          <div>Revenue: <strong>{fmt$(q.job.total_revenue)}</strong></div>
                          <div>Cost: {fmt$(q.job.total_cost)}</div>
                          <div>Profit: <strong>{fmt$(q.job.profit)}</strong> ({q.job.profit_pct?.toFixed(1)}%)</div>
                        </div>
                      </div>
                    )}

                    {q.description && (
                      <div style={{ marginBottom: 6 }}>
                        <div style={{ fontWeight: 600, color: C.text, fontSize: 11, marginBottom: 2 }}>Description:</div>
                        <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{q.description}</div>
                      </div>
                    )}

                    {(q.job?.line_items_text || q.quote_line_items) && (
                      <details open={!!q.job?.line_items_text}>
                        <summary style={{ cursor: "pointer", color: C.accent, fontSize: 11, fontWeight: 600, marginBottom: 4 }}>
                          {q.job?.line_items_text ? "Job line items (actual)" : "Quote line items"}
                        </summary>
                        <pre style={{
                          background: C.bg, padding: 10, borderRadius: 6,
                          fontFamily: "ui-monospace, SFMono-Regular, monospace",
                          fontSize: 10, lineHeight: 1.5, color: C.text,
                          margin: "6px 0 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word",
                          maxHeight: 320, overflowY: "auto",
                        }}>
                          {q.job?.line_items_text || q.quote_line_items}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ fontSize: 10, color: C.muted, marginTop: 10, fontStyle: "italic" }}>
          Pulled from jb_quotes + jb_jobs. Match logic: jb_quotes.service_type AND/OR title keywords for this family. {history.summary.matched > 100 && "Showing first 100 — refine the family keyword map in api/quote-brain.js if you need more."}
        </div>
      </div>
    </div>
  );
}

function VerdictBadge({ v }) {
  const map = {
    WON:   { c: C.good,   t: "WON"  },
    LOST:  { c: C.danger, t: "LOST" },
    OPEN:  { c: C.warn,   t: "OPEN" },
    OTHER: { c: C.muted,  t: "—"    },
  };
  const m = map[v] || map.OTHER;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      padding: "2px 6px", borderRadius: 999,
      background: `${m.c}1f`, color: m.c, fontSize: 9, fontWeight: 700, letterSpacing: "0.05em",
    }}>{m.t}</span>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div style={{ background: C.surface2, padding: 10, borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 16, color: C.text, fontWeight: 700, marginTop: 2 }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{hint}</div>}
    </div>
  );
}

// ── Rules library panel (from playbook.meta.rules_library + pitfalls) ──────
function RulesLibrary({ playbook }) {
  const meta = playbook?.meta || {};
  const rules    = Array.isArray(meta.rules_library) ? meta.rules_library : [];
  const pitfalls = Array.isArray(meta.pitfalls)      ? meta.pitfalls      : [];
  const range    = meta.typical_price_range;
  if (!rules.length && !pitfalls.length && !range) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <SectionTitle>📚 Rules Library & Pitfalls</SectionTitle>
      <div style={cardStyle}>
        {range && Array.isArray(range) && range.length === 2 && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
            <strong style={{ color: C.text }}>Typical price range:</strong> ${range[0].toLocaleString("en-CA")} – ${range[1].toLocaleString("en-CA")}
          </div>
        )}

        {rules.length > 0 && (
          <div style={{ marginBottom: pitfalls.length ? 14 : 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
              Decision Rules — "If This, Then That"
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {rules.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "8px 10px", background: C.surface2, borderRadius: 8, fontSize: 12 }}>
                  <div style={{ minWidth: 220, flex: "0 0 240px", color: C.warn }}>
                    <span style={{ fontWeight: 600 }}>IF</span> {r.trigger}
                  </div>
                  <div style={{ flex: 1, color: C.text }}>
                    <span style={{ fontWeight: 600, color: C.accent }}>→ </span>{r.action}
                    {r.why && <span style={{ color: C.muted, marginLeft: 6 }}>({r.why})</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {pitfalls.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
              Common Pitfalls
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
              {pitfalls.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        )}

        {meta.skeleton === true && (
          <div style={{ marginTop: 12, padding: 10, background: "rgba(245,158,11,0.08)", border: `1px solid rgba(245,158,11,0.3)`, borderRadius: 8, fontSize: 11, color: C.warn }}>
            ⚠️ <strong>Skeleton template.</strong> Starter rules + placeholder pricing. Customize the material nodes (Edit buttons) before relying on this for real quotes.
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tiny helpers / sub-components ───────────────────────────────────────────
function Header({ title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.03em" }}>
        {title}
      </div>
      {subtitle && <div style={{ fontSize: 13, color: C.muted, marginTop: 6, maxWidth: 720 }}>{subtitle}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
      {children}
    </div>
  );
}

function Pill({ color, children }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "3px 8px", borderRadius: 999,
      background: `${color}1f`, color, fontSize: 11, fontWeight: 600,
    }}>{children}</span>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
        {label}
      </div>
      {children}
    </label>
  );
}

function Check({ label, value, onChange }) {
  return (
    <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

function Row({ label, children, strong }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", margin: "2px 0", color: strong ? C.text : C.muted, fontWeight: strong ? 700 : 400 }}>
      <span>{label}</span>
      <span>{children}</span>
    </div>
  );
}

function priceSourceColor(ps) {
  const tier = typeof ps === "string" ? ps : ps?.tier;
  switch (tier) {
    case "supplier_prices":  return C.good;
    case "manual_pin":       return "#3b82f6";
    case "memory_override":  return "#a78bfa";
    case "org_price_list":   return "#fbbf24";
    case "logbook_history":  return "#2dd4bf";
    case "allowance":        return "#94a3b8";
    case "breaker_package":  return C.warn;
    case "template_default": return C.warn;
    case "web_placeholder":  return C.danger;
    default:                 return C.muted;
  }
}
function priceSourceDot(ps) {
  const tier = typeof ps === "string" ? ps : ps?.tier;
  return {
    supplier_prices:  "🟢",
    manual_pin:       "🔵",
    memory_override:  "🟣",
    org_price_list:   "🟡",
    logbook_history:  "📗",
    allowance:        "⚪",
    breaker_package:  "🤖",
    template_default: "🟠",
    web_placeholder:  "🔴",
  }[tier] || "·";
}

function prettifyFamily(f) {
  return String(f || "")
    .split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
    .replace(/^Ev /, "EV ");
}

// ── Styles ──────────────────────────────────────────────────────────────────
const pageStyle = {
  padding:     "20px",
  minHeight:   "100vh",
  background:  C.bg,
  color:       C.text,
  fontFamily:  "'Roboto', sans-serif",
};
const cardStyle = {
  background:  C.surface,
  border:      `1px solid ${C.border}`,
  borderRadius:12,
  padding:     14,
};
const sectionHeadStyle = {
  fontSize:        13,
  fontWeight:      700,
  color:           C.muted,
  textTransform:   "uppercase",
  letterSpacing:   "0.06em",
  marginBottom:    8,
};
const subheadStyle = {
  fontSize:        11,
  fontWeight:      700,
  color:           C.muted,
  textTransform:   "uppercase",
  letterSpacing:   "0.06em",
  marginBottom:    6,
};
const inputStyle = {
  width:        "100%",
  padding:      "8px 10px",
  background:   C.surface2,
  border:       `1px solid ${C.border}`,
  borderRadius: 6,
  color:        C.text,
  fontSize:     13,
  fontFamily:   "inherit",
  outline:      "none",
  boxSizing:    "border-box",
};
function btn(variant = "default", disabled = false) {
  const base = {
    border:    `1px solid ${C.border}`,
    background: C.surface2,
    color:      C.text,
    padding:    "8px 14px",
    borderRadius: 8,
    fontSize:   13,
    fontWeight: 600,
    cursor:     disabled ? "not-allowed" : "pointer",
    opacity:    disabled ? 0.5 : 1,
    fontFamily: "inherit",
  };
  if (variant === "primary") return { ...base, background: C.accent, color: C.bg, border: "none" };
  if (variant === "ghost")   return { ...base, background: "transparent" };
  return base;
}
